import React, {useMemo, useState, useEffect, useRef, useCallback} from "react";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, pointerWithin, rectIntersection, useDroppable, MeasuringStrategy } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useParams, useNavigate } from "react-router-dom";
import ContentBox from "../components/ContentBox";
import HabboImage from "../components/HabboImage";
import {
    getProfilePageLoad,
    getProfileRelationship,
    sendProfileFriendRequest,
    removeProfileFriend,
    blockProfileUser,
    unblockProfileUser,
    getProfileData,
    upsertOwnProfileData,
    getProfileAchievements,
    listProfileFriends,
    listProfilePosts,
    createProfilePost,
    hideProfilePost,
    listPostComments,
    createPostComment,
    hideProfileComment,
    createProfileEngagement,
    listProfileEngagements,
    deleteProfileEngagement,
    getActiveRestrictions,
    getActiveBan,
    listMyGivenEngagementsRecent,
    getProfileEngagementSummary,
    getTopProfileFriends,
    getProfileFriendSuggestions,
    getMagicWardrobeProfile,
    getMyCircleBgs,
} from "../services/api";
import { awardOwnProfileAchievement } from '../services/api';
import { getImagerBlob, getMyImagerScenes } from '../services/api';
import { getFranklyCloudItemsForUser } from '../services/api';
import { renderSceneToCanvas } from '../utils/sceneRenderer';
import { decodeCloudItem } from '../utils/cloudItemUtils';
import { fetchProfileColor, invalidateProfileColor, getAvatarBgStyle, safeHexOrFallback } from '../services/profileColorService';
import {
    loadEmoteContext,
    useEmoteContext,
    renderEmotesInHtml,
    renderEmotesInHtmlMemo,
    unrenderEmotesFromHtml,
    stripEmoteSentinels,
    EmoteImg,
    EmotePickerModal,
    PlainEmoteInput,
    RichTextEmoteInput,
} from '../services/emoteService';
import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import StatusBox from '../components/profile/StatusBox';
import BioBox from '../components/profile/BioBox';
import ReportProfileModal from '../components/profile/ReportProfileModal';
import FancyFontBox from '../components/profile/FancyFontBox';
import EditableBox from '../components/profile/EditableBox';
import LuvBox from '../components/profile/LuvBox';
import AchievementsBox from '../components/profile/AchievementsBox';
import YouTubeBox from '../components/profile/YouTubeBox';
import GalleryBox from '../components/profile/GalleryBox';
import HabbstagramBox from '../components/profile/HabbstagramBox';
import ColorPicker from '../components/ColorPicker';
import FranklyCloudModal, { PannableImage, ImageLightbox } from '../components/FranklyCloudModal';
import PostItem, { replyCache, replyInflight } from '../components/profile/PostItem';
import { getPresetBackgrounds, isSkinSaveable } from '../data/profileSkins';
import { resolveCosmeticUrl } from '../services/profileColorService';


const MAGIC_WARDROBE_COOLDOWN_MS = 3000;
const MAX_GALLERY_BOXES = 5;
const GALLERY_KEY_PREFIX = 'gallery_';
const isGalleryKey = (k) => typeof k === 'string' && k.startsWith(GALLERY_KEY_PREFIX);
// ─────────────────────────────────────────────────────────────────────────────

const RL_WINDOW_MS = 60 * 60 * 1000;
const RL_MAX = 3;
const RL_COOLDOWN_MS = 15 * 60 * 1000;
const RL_BURST_MS = 30 * 1000;
const RL_BURST_EXEMPT_GAP_MS = 60 * 60 * 1000;

const POST_MAX_CHARS = 500;
const POST_MAX_LINES = 10;


const DEFAULT_LUV_HEART_COLOUR = "#e72354";

const normalizeHtml = (html) =>
    String(html ?? "").replace(/\u200B/g, "").replace(/\uFEFF/g, "").replace(/&nbsp;/g, " ").replace(/&lt;3/g, "<3");

const formatContent = (html, emoteCtx) => {
    const safe = normalizeHtml(html || "");
    return renderEmotesInHtmlMemo(safe, emoteCtx);
};

const RichTextInput = RichTextEmoteInput;


const GENERIC_CACHE_TTL_MS = 30 * 60 * 1000;

const getCachedData = (key) => {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (Date.now() - parsed.ts > GENERIC_CACHE_TTL_MS) return null;
        return parsed.data;
    } catch {
        return null;
    }
};

const setCachedData = (key, data) => {
    try {
        sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
    } catch {}
};
const clearCachedData = (key) => {
    try { sessionStorage.removeItem(key); } catch {}
};

const clamp01 = (n) => Math.max(0, Math.min(1, Number(n)));

const hexToRgb = (hex) => {
    const h = String(hex || '').trim();
    if (!/^#[0-9A-Fa-f]{6}$/.test(h)) return { r: 255, g: 255, b: 255 };
    return { r: parseInt(h.slice(1, 3), 16), g: parseInt(h.slice(3, 5), 16), b: parseInt(h.slice(5, 7), 16) };
};

const rgbToHex = ({ r, g, b }) => {
    const to = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
    return `#${to(r)}${to(g)}${to(b)}`;
};

const mixToWhite = (hex, amountWhite) => {
    const t = clamp01(amountWhite);
    const c = hexToRgb(hex);
    return rgbToHex({ r: c.r + (255 - c.r) * t, g: c.g + (255 - c.g) * t, b: c.b + (255 - c.b) * t });
};

const srgbToLinear = (v) => { const x = v / 255; return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4); };

const luminance = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
};

const contrastRatio = (bgHex, textHex) => {
    const L1 = luminance(bgHex); const L2 = luminance(textHex);
    return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
};

const ensureReadableBgForDarkText = (bgHex, textHex = '#222222', minRatio = 4.5) => {
    let bg = String(bgHex || '#ffffff');
    for (let i = 0; i < 20; i++) { if (contrastRatio(bg, textHex) >= minRatio) return bg; bg = mixToWhite(bg, 0.08); }
    return bg;
};

const buildOnMyMindPalette = (headerHex) => {
    const raw = String(headerHex || '').trim();
    if (!/^#[0-9A-Fa-f]{6}$/.test(raw)) return { postBg: '#f2f2f2', commentsAreaBg: '#e9e9e9', commentBg: '#dedede' };
    return {
        postBg: ensureReadableBgForDarkText(mixToWhite(raw, 0.84)),
        commentsAreaBg: ensureReadableBgForDarkText(mixToWhite(raw, 0.80)),
        commentBg: ensureReadableBgForDarkText(mixToWhite(raw, 0.74)),
    };
};


const encodeEmojis = (s) => String(s ?? '').replace(/[\u{80}-\u{10FFFF}]/gu, (ch) => `&#${ch.codePointAt(0)};`);
const isSafeExternalUrl = (url) => {
    try {
        const u = new URL(url);
        return u.protocol === "http:" || u.protocol === "https:";
    } catch {
        return false;
    }
};

const ExternalLinkModal = ({ url, onClose, onContinue }) => {
    if (!url) return null;
    return (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/30 p-3">
            <div className="w-full max-w-md border border-black/40 bg-offwhite rounded shadow-md overflow-hidden">
                <div className="px-3 py-2 border-b border-black/20 font-bold text-coal">External link</div>
                <div className="p-3 text-sm text-coal">
                    <div className="mb-2">
                        This link goes to an external web page. Frankly cannot guarantee it&apos;s safety. Be cautious, avoid being scammed.
                    </div>
                    <div className="text-[11px] text-coal/70 break-all border border-black/20 bg-white/50 p-2 rounded">
                        {url}
                    </div>
                    <div className="mt-3">Do you want to continue?</div>
                </div>
                <div className="p-2 border-t border-black/20 flex justify-end gap-2 bg-black/5">
                    <button type="button" onClick={onClose} className="px-3 py-1 border border-black/40 bg-offwhite text-coal text-sm rounded hover:bg-black/5">No</button>
                    <button type="button" onClick={onContinue} className="px-3 py-1 border border-black/40 bg-gold text-coal text-sm rounded hover:brightness-95">Yes</button>
                </div>
            </div>
        </div>
    );
};

const checkRateLimitFromTimestamps = (timestamps) => {
    const now = Date.now();
    const recent = timestamps
        .map(t => (t instanceof Date ? t.getTime() : typeof t === 'string' ? new Date(t).getTime() : Number(t)))
        .filter(t => Number.isFinite(t) && now - t < RL_WINDOW_MS)
        .sort((a, b) => b - a);

    const last = recent[0] ?? null;
    const secondLast = recent[1] ?? null;

    if (last !== null && now - last < RL_BURST_MS) {
        const gapBeforeLast = secondLast !== null ? last - secondLast : Infinity;
        if (gapBeforeLast < RL_BURST_EXEMPT_GAP_MS) {
            const waitSec = Math.ceil((RL_BURST_MS - (now - last)) / 1000);
            return { allowed: false, reason: `Please wait ${waitSec} second${waitSec === 1 ? '' : 's'} before posting again.` };
        }
    }

    if (recent.length >= RL_MAX) {
        const oldest = Math.min(...recent);
        const cooldownEnds = oldest + RL_WINDOW_MS + RL_COOLDOWN_MS;
        if (now < cooldownEnds) {
            const waitMin = Math.ceil((cooldownEnds - now) / 60000);
            return { allowed: false, reason: `You've been too active recently. Please wait ${waitMin} minute${waitMin === 1 ? '' : 's'}.` };
        }
    }

    return { allowed: true };
};

const blockConfirmBody = (user) => (
    <div style={{ whiteSpace: "normal" }}>
        <div>Blocking a user on Frankly does the following:</div>
        <ul style={{ margin: "8px 0 8px 18px" }}>
            <li>Removes all interactions with <strong>{user}</strong> from your page.</li>
            <li>Stops <strong>{user}</strong> seeing your profile.</li>
            <li>Stops you interacting on <strong>{user}</strong>&apos;s page.</li>
        </ul>
        <div style={{ marginTop: 8 }}>
            Note: While blocking is reversible by visiting that user&apos;s page, the removal of all interactions (comments, etc.) is permanent and cannot be reversed.
        </div>
        <div style={{ marginTop: 12 }}>
            Are you sure you want to block <strong>{user}</strong>?
        </div>
    </div>
);

const unblockConfirmBody = (user) => (
    <div style={{ whiteSpace: "normal" }}>
        <div>Unblocking a user on Frankly does the following:</div>
        <ul style={{ margin: "8px 0 8px 18px" }}>
            <li>Allows <strong>{user}</strong> to see your profile again.</li>
            <li>Allows you to interact on <strong>{user}</strong>&apos;s page again.</li>
            <li>Allows new interactions with <strong>{user}</strong> to appear on your page again.</li>
        </ul>
        <div style={{ marginTop: 8 }}>
            Note: Any interactions removed at the time of blocking (comments, etc.) remain permanently removed and will not be restored by unblocking.
        </div>
        <div style={{ marginTop: 12 }}>
            Are you sure you want to unblock <strong>{user}</strong>?
        </div>
    </div>
);

const getLikesCount = (obj) => {
    const v = obj?.likesCount ?? obj?.likes_count ?? obj?.likescount ?? obj?.LikesCount ?? obj?.likes ?? obj?.Likes ?? 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const SortableBox = ({ id, columnId, isEditing, children, onHeaderProps }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, data: { columnId } });
    const style = {
        transform: transform ? `translate3d(0px, ${transform.y}px, 0)` : undefined,
        transition,
        opacity: isDragging ? 0.35 : 1,
    };
    return (
        <div ref={setNodeRef} style={style}>
            {React.cloneElement(children, {
                dragEnabled: isEditing,
                dragHandleProps: isEditing ? { ...attributes, ...listeners } : {},
                dragOver: false,
                editingHeader: onHeaderProps?.editingHeader,
                setEditingHeader: onHeaderProps?.setEditingHeader,
                headerTitle: onHeaderProps?.getHeaderTitle?.(id),
                setHeaderTitle: onHeaderProps?.setHeaderTitle,
                ...onHeaderProps,
            })}
        </div>
    );
};

const SortableFriendItem = ({ friend, isEditing, isOwnProfile, avatarSize, onClick }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: friend.userID, disabled: !isEditing || !isOwnProfile });
    const bg = friend.profileBg || '#5A2929';
    const hexBg = safeHexOrFallback(bg);
    const textColor = luminance(hexBg) < 0.16 ? '#f5f5f0' : '#222222';
    const style = { ...getAvatarBgStyle(bg), transform: CSS.Translate.toString(transform), transition, aspectRatio: avatarSize === "a" ? '3 / 4' : '1 / 1', opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 100 : 1 };
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...(isEditing && isOwnProfile ? listeners : {})} onClick={onClick} className={`flex flex-col items-center justify-center overflow-hidden relative ${isEditing && isOwnProfile ? 'cursor-move hover:brightness-90' : 'cursor-pointer hover:brightness-95'}`} title={friend.habboName}>
            <div className="relative flex items-center justify-center top-[-10px] pointer-events-none">
                <HabboImage habbo={friend.habboName} hotel="COM" size={avatarSize === "a" ? "a" : "b"} direction={2} head_direction={3} gesture="sml" style={{ imageRendering: "pixelated", display: "block" }} />
            </div>
            <div className="absolute bottom-2 left-0 right-0 font-['Jersey_10'] text-lg text-center leading-none truncate px-0.5 pointer-events-none tracking-widest" style={{ color: textColor }}>{friend.habboName}</div>
        </div>
    );
};

function ColumnDroppable({ id, children }) {
    const { setNodeRef } = useDroppable({ id });
    return <div ref={setNodeRef}>{children}</div>;
}

const normalizePost = (p) => p ? { ...p, id: p.id ?? p.ID, likesCount: getLikesCount(p) } : p;


const OnMyMindContent = ({ profileUsername, isOwnProfile, userData, timeAgo, headerColour, mentionFriends, restrictPosting, restrictCommenting, restrictLiking, restrictionExpiry, isSteve, isGuest, previewMode, initialPostsData, isNarrowColumn, cloudUploadMode }) => {
    const [posts, setPosts] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [postsHasMore, setPostsHasMore] = React.useState(false);
    const [postsOffset, setPostsOffset] = React.useState(0);
    const [postsLoadingMore, setPostsLoadingMore] = React.useState(false);
    const POSTS_PAGE_SIZE = 20;

    const emoteCtx = useEmoteContext() ?? { packs: [], emotesByPack: {} };

    const [newPostContent, setNewPostContent] = React.useState("");
    const [showImagePasteWarning, setShowImagePasteWarning] = React.useState(false);
    const handleNewPostContentChange = React.useCallback((html) => {
        const htmlStr = String(html ?? '');
        if (/<img[\s>]/i.test(htmlStr)) {
            // Don't warn for fc-images (FranklyCloud) or emotes — only for pasted external images
            const parser = new DOMParser();
            const doc = parser.parseFromString(`<div>${htmlStr}</div>`, 'text/html');
            const hasExternalImg = Array.from(doc.querySelectorAll('img')).some(
                img => !img.classList.contains('fc-image') && !img.classList.contains('inline-emote')
            );
            setShowImagePasteWarning(hasExternalImg);
        } else {
            setShowImagePasteWarning(false);
        }
        setNewPostContent(html);
    }, []);
    const [likedPostIds, setLikedPostIds] = React.useState(new Set());
    const [likedCommentIds, setLikedCommentIds] = React.useState(new Set());
    const [likeCountsByPostId, setLikeCountsByPostId] = React.useState({});
    const [likeCountsByCommentId, setLikeCountsByCommentId] = React.useState({});
    const [confirmState, setConfirmState] = React.useState(null);
    const [commentTimestampsByUser, setCommentTimestampsByUser] = React.useState({});
    const [postMetrics, setPostMetrics] = React.useState({ chars: 0, lines: 1 });

    const currentUserId = String(userData?.id ?? userData?.ID ?? userData?.userId ?? userData?.userID ?? userData?.UserID ?? userData?.user_id ?? '');
    const currentUserHabboName = userData?.habboName || userData?.HabboName || userData?.username || userData?.name || '';
    const palette = React.useMemo(() => buildOnMyMindPalette(headerColour), [headerColour]);

    const [isEmotePickerOpen, setIsEmotePickerOpen] = React.useState(false);
    const emoteInsertRef = React.useRef(null);

    const openEmotePicker = React.useCallback((insertFn) => {
        emoteInsertRef.current = insertFn;
        setIsEmotePickerOpen(true);
    }, []);

    const closeEmotePicker = React.useCallback(() => setIsEmotePickerOpen(false), []);

    const handleEmoteSelected = React.useCallback((token) => {
        if (typeof emoteInsertRef.current === "function") {
            emoteInsertRef.current(String(token || ""));
        }
        setIsEmotePickerOpen(false);
    }, []);

    const [isFranklyCloudOpen, setIsFranklyCloudOpen] = React.useState(false);
    const [postImages, setPostImages] = React.useState([]);
    const [editorLightboxItem, setEditorLightboxItem] = React.useState(null);
    const [cloudItemsMap, setCloudItemsMap] = React.useState({});
    const [cloudItemsMapReady, setCloudItemsMapReady] = React.useState(false);

    React.useEffect(() => {
        if (!profileUsername) return;
        const apiOrigin = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '');
        const resolveUpload = (src) => (!src || src.startsWith('data:') || src.startsWith('http') ? src : `${apiOrigin}${src.startsWith('/') ? '' : '/'}${src}`);
        getFranklyCloudItemsForUser(profileUsername)
            .then(async data => {
                if (data?.success && Array.isArray(data.items)) {
                    const map = {};
                    await Promise.all(data.items.map(async item => {
                        const decoded = item.type === 'upload' ? { ...item, src: resolveUpload(item.src) } : await decodeCloudItem(item);
                        map[decoded.id] = decoded;
                    }));
                    setCloudItemsMap(map);
                }
            })
            .catch(() => {})
            .finally(() => setCloudItemsMapReady(true));
    }, [profileUsername]);

    React.useEffect(() => {
        const onDeleted = (e) => {
            const itemId = e.detail?.itemId;
            if (itemId == null) return;
            setCloudItemsMap(prev => ({ ...prev, [itemId]: { deleted: true } }));
        };
        window.addEventListener('frankly:cloudImageDeleted', onDeleted);
        return () => window.removeEventListener('frankly:cloudImageDeleted', onDeleted);
    }, []);

    const openFranklyCloud = React.useCallback(() => {
        setIsFranklyCloudOpen(true);
    }, []);

    const handleCloudInsert = React.useCallback((item) => {
        setPostImages(prev => prev.length < 4 ? [...prev, item] : prev);
        if (item?.id != null) setCloudItemsMap(prev => ({ ...prev, [item.id]: item }));
        setIsFranklyCloudOpen(false);
    }, []);


    const requestConfirm = ({ title, message, onConfirm }) => setConfirmState({ title, message, onConfirm });
    const closeConfirm = () => setConfirmState(null);
    const runConfirm = async () => { const fn = confirmState?.onConfirm; closeConfirm(); if (typeof fn === 'function') await fn(); };

    const [refreshCooldown, setRefreshCooldown] = React.useState(false);

    const fetchPosts = async (mode = 'merge', offset = 0) => {
        if (mode === 'reset') { setLoading(true); setPostsOffset(0); }
        if (mode === 'append') setPostsLoadingMore(true);
        try {
            const postsRes = await listProfilePosts(profileUsername, POSTS_PAGE_SIZE, offset);
            const loadedPosts = postsRes?.posts ? postsRes.posts.map(p => normalizePost(p)) : [];

            setPostsHasMore(Boolean(postsRes?.hasMore));

            const postCounts = {};
            loadedPosts.forEach(p => {
                const id = String(p.id || '');
                if (!id) return;
                postCounts[id] = getLikesCount(p);
            });

            const commentCounts = postsRes?.commentLikeCounts || {};
            const cLikes = new Set(Object.keys(postsRes?.commentViewerLiked || {}));
            const pLikes = new Set(
                loadedPosts.filter(p => p.isLikedByViewer).map(p => String(p.id))
            );

            if (mode === 'append') {
                setPosts(prev => {
                    const existingIds = new Set((Array.isArray(prev) ? prev : []).map(p => String(p.id || p.ID || '')));
                    const newPosts = loadedPosts.filter(p => !existingIds.has(String(p.id || p.ID || '')));
                    return [...(Array.isArray(prev) ? prev : []), ...newPosts];
                });
                setLikeCountsByPostId(prev => ({ ...prev, ...postCounts }));
                setLikeCountsByCommentId(prev => ({ ...prev, ...commentCounts }));
                setLikedPostIds(prev => new Set([...prev, ...pLikes]));
                setLikedCommentIds(prev => new Set([...prev, ...cLikes]));
                setPostsOffset(offset);
            } else {
                setPosts(prev => {
                    const prevById = new Map((Array.isArray(prev) ? prev : []).map(p => [String(p.id || p.ID || ''), p]));
                    return loadedPosts.map(next => {
                        const id = String(next.id || next.ID || '');
                        const existing = prevById.get(id);
                        if (existing && existing.content === next.content) return existing;
                        return next;
                    });
                });
                setLikeCountsByPostId(postCounts);
                setLikeCountsByCommentId(commentCounts);
                setLikedPostIds(pLikes);
                setLikedCommentIds(cLikes);
            }
        } catch {
            toast.error("Failed to load posts.");
        } finally {
            setLoading(false);
            setPostsLoadingMore(false);
        }
    };

    React.useEffect(() => {
        if (!profileUsername) return;
        if (initialPostsData && Array.isArray(initialPostsData.posts)) {
            const loadedPosts = initialPostsData.posts.map(p => normalizePost(p));
            const postCounts = {};
            loadedPosts.forEach(p => {
                const id = String(p.id || '');
                if (id) postCounts[id] = getLikesCount(p);
            });
            setPosts(loadedPosts);
            setPostsHasMore(Boolean(initialPostsData.hasMore));
            setPostsOffset(0);
            setLikeCountsByPostId({ ...postCounts, ...(initialPostsData.postLikeCounts || {}) });
            setLikeCountsByCommentId(initialPostsData.commentLikeCounts || {});
            setLikedPostIds(new Set(Object.keys(initialPostsData.postViewerLiked || {})));
            setLikedCommentIds(new Set(Object.keys(initialPostsData.commentViewerLiked || {})));
            setLoading(false);
        } else {
            fetchPosts('reset', 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileUsername, currentUserId]);

    const checkPostRateLimit = () => {
        const postTimestamps = posts.map(p => p.createdAt).filter(Boolean);
        return checkRateLimitFromTimestamps(postTimestamps);
    };

    const checkCommentRateLimit = (commenterName) => {
        const name = String(commenterName || '').trim().toLowerCase();
        const timestamps = commentTimestampsByUser[name] || [];
        return checkRateLimitFromTimestamps(timestamps);
    };

    const [externalLinkUrl, setExternalLinkUrl] = React.useState("");
    const openExternalLink = (url) => setExternalLinkUrl(String(url || ""));
    const closeExternalLink = () => setExternalLinkUrl("");
    const continueExternalLink = () => {
        const url = externalLinkUrl;
        closeExternalLink();
        if (isSafeExternalUrl(url)) window.open(url, "_blank", "noopener,noreferrer");
    };

    const recordNewComment = (commenterName, timestamp) => {
        const name = String(commenterName || '').trim().toLowerCase();
        if (!name) return;
        setCommentTimestampsByUser(prev => ({
            ...prev,
            [name]: [...(prev[name] || []), timestamp],
        }));
    };

    const handlePost = async () => {
        if (!isOwnProfile) return;

        if (restrictPosting) {
            const expiry = restrictionExpiry ? ` until ${new Date(restrictionExpiry).toLocaleString()}` : '';
            toast.warning(`You are restricted from posting${expiry}.`);
            return;
        }

        if (!isSteve && postOverLimit) {
            toast.warning(`Post exceeds limits (${postMetrics.chars}/${POST_MAX_CHARS} chars, ${postMetrics.lines}/${POST_MAX_LINES} lines).`);
            return;
        }

        const html = String(newPostContent ?? "").replace(/\r/g, "").trim();
        if (!html || (postMetrics?.chars ?? 0) <= 0) {
            toast.warning("Please add some text to your post.");
            return;
        }

        if (!isSteve) {
            const rl = checkPostRateLimit();
            if (!rl.allowed) {
                toast.warning(rl.reason);
                return;
            }
        }

        const now = new Date().toISOString();
        let encoded = encodeEmojis(html);
        if (postImages.length > 0) {
            const refs = postImages.map(img => ({
                id: img.id,
                type: img.type,
                name: img.name,
                ...(img.type === 'upload' && img.src ? { src: img.src } : {}),
            }));
            encoded += `<div class="fc-attachment-block" data-images='${JSON.stringify(refs).replace(/'/g, '&#39;')}'></div>`;
        }

        const optimistic = normalizePost({
            id: `optimistic-post-${Date.now()}`,
            content: encoded,
            createdAt: now,
            likesCount: 0,
            Engagements: [],
            comments: [],
            isOptimistic: true,
        });

        setPosts((prev) => [optimistic, ...(Array.isArray(prev) ? prev : [])]);
        setNewPostContent("");
        setPostImages([]);

        try {
            const arity = typeof createProfilePost === "function" ? Number(createProfilePost.length) : 0;
            const res = arity >= 2
                ? await createProfilePost(profileUsername, encoded)
                : await createProfilePost(encoded);

            if (!res?.success) throw new Error(res?.message || "Failed");
            await fetchPosts('reset', 0);
        } catch {
            setPosts((prev) => prev.filter((p) => String(p.id || p.ID) !== String(optimistic.id)));
            toast.error("Failed to create post. Please try again.");
        }
    };

    const handleDeletePost = async (postId) => {
        try {
            await hideProfilePost(postId);
            setPosts(prev => prev.filter(p => String(p.id || p.ID) !== String(postId)));
        } catch { toast.error("Failed to delete post. Please try again."); }
    };

    const handleBlockUser = async (habboName) => {
        try {
            await blockProfileUser(habboName);
            window.location.reload();
        } catch {
            toast.error("Failed to block user. Please try again.");
        }
    };

    const postLimitToastRef = React.useRef(false);

    const postOverLimit = postMetrics.chars > POST_MAX_CHARS || postMetrics.lines > POST_MAX_LINES;

    React.useEffect(() => {
        if (!isSteve && postOverLimit && !postLimitToastRef.current) {
            toast.warning(`Post exceeds limits (${postMetrics.chars}/${POST_MAX_CHARS} chars, ${postMetrics.lines}/${POST_MAX_LINES} lines).`);
            postLimitToastRef.current = true;
        }

        if (!postOverLimit) {
            postLimitToastRef.current = false;
        }
    }, [isSteve, postOverLimit, postMetrics]);

    const handleRefreshPosts = async () => {
        if (refreshCooldown) return;

        setRefreshCooldown(true);

        try {
            Object.keys(replyCache).forEach((k) => { delete replyCache[k]; });
            Object.keys(replyInflight).forEach((k) => { delete replyInflight[k]; });
            await fetchPosts('reset', 0);
        } catch {
            toast.error("Failed to refresh posts.");
        } finally {
            setTimeout(() => setRefreshCooldown(false), 5000);
        }
    };

    return (
        <div className="w-full max-w-full flex flex-col max-h-[600px] overflow-hidden relative">
            <style>{`
                .habbo-scroll::-webkit-scrollbar { width: 16px; height: 16px; }
                .habbo-scroll::-webkit-scrollbar-track { background: #cfcfcf; border: 1px solid #000; }
                .habbo-scroll::-webkit-scrollbar-thumb { background: #8f8f8f; border: 1px solid #000; }
                .habbo-scroll::-webkit-scrollbar-thumb:hover { background: #7a7a7a; }
            `}</style>
            <EmotePickerModal open={isEmotePickerOpen} onClose={closeEmotePicker} onSelect={handleEmoteSelected}  emoteCtx={emoteCtx} />

            {isOwnProfile && (
                <div className="flex flex-col border-b border-black/30">
                    {restrictPosting ? (
                        <div className="p-3 text-xs text-coal/70">
                            {isGuest ? 'Login to post.' : <>You are restricted from posting{restrictionExpiry ? ` until ${new Date(restrictionExpiry).toLocaleString()}` : ''}.</>}
                        </div>
                    ) : (
                        <>
                            {showImagePasteWarning && (
                                <div className="p-3 bg-yellow-100 border-l-4 border-yellow-500 text-xs text-yellow-800">
                                    Images can't be embedded in posts — only text and emotes are supported.
                                </div>
                            )}
                            <RichTextInput
                                value={newPostContent}
                                onChange={handleNewPostContentChange}
                                placeholder="What's on your mind?"
                                isOwnProfile={isOwnProfile}
                                emoteCtx={emoteCtx}
                                maxChars={POST_MAX_CHARS}
                                maxLines={POST_MAX_LINES}
                                onMetrics={setPostMetrics}
                                onOpenEmotePicker={openEmotePicker}
                                onOpenFranklyCloud={isOwnProfile ? openFranklyCloud : undefined}
                                onRefresh={handleRefreshPosts}
                                refreshCount={0}
                                refreshDisabled={refreshCooldown}
                                attachmentSlot={postImages.length > 0 ? (
                                    <div className="flex flex-row flex-wrap gap-1 p-1">
                                        {postImages.map(img => (
                                            <div
                                                key={img.id}
                                                className="relative group flex-none w-14 h-14 rounded overflow-hidden border border-black/20 cursor-pointer"
                                                onClick={() => setEditorLightboxItem(img)}
                                            >
                                                <img
                                                    src={img.src}
                                                    alt={img.name || ''}
                                                    className="w-full h-full object-cover"
                                                    draggable={false}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); setPostImages(prev => prev.filter(i => i.id !== img.id)); }}
                                                    className="absolute top-0.5 right-0.5 w-4 h-4 rounded-sm bg-black/70 text-white text-[9px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity leading-none"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            />
                        </>
                    )}

                    <div className="bg-black/10 p-2 flex justify-between items-center">
                        {postImages.length > 0 ? (
                            <span className="text-[10px] text-coal/50">{postImages.length} image{postImages.length !== 1 ? 's' : ''} attached</span>
                        ) : <span />}
                        <button
                            type="button"
                            onClick={handlePost}
                            disabled={postOverLimit || restrictPosting}
                            className={`px-4 py-1 border border-black/50 text-coal text-sm rounded transition-colors ${
                                restrictPosting
                                    ? 'opacity-40 cursor-not-allowed bg-black/10'
                                    : postOverLimit
                                        ? 'opacity-40 cursor-not-allowed bg-black/10'
                                        : 'bg-offwhite hover:bg-gold'
                            }`}
                        >
                            Post
                        </button>
                    </div>
                </div>
            )}

            <div className="habbo-scroll overflow-y-auto flex-1">
                {loading ? (
                    <div className="p-3 text-coal/70">Loading posts...</div>
                ) : posts.length === 0 ? (
                    <div className="p-3 text-coal/70">{isOwnProfile ? "You haven't posted anything yet!" : `${profileUsername} hasn't posted anything yet.`}</div>
                ) : (
                    posts.map(post => (
                        <PostItem
                            key={post.id || post.ID}
                            post={post}
                            profileUsername={profileUsername}
                            isOwnProfile={isOwnProfile}
                            userData={userData}
                            timeAgo={timeAgo}
                            onDeletePost={handleDeletePost}
                            onBlockUser={handleBlockUser}
                            likedPostIds={likedPostIds}
                            likedCommentIds={likedCommentIds}
                            likeCountsByPostId={likeCountsByPostId}
                            likeCountsByCommentId={likeCountsByCommentId}
                            requestConfirm={requestConfirm}
                            palette={palette}
                            checkCommentRateLimit={checkCommentRateLimit}
                            recordNewComment={recordNewComment}
                            currentUserHabboNameOverride={currentUserHabboName}
                            mentionFriends={mentionFriends}
                            onExternalLink={openExternalLink}
                            onOpenEmotePicker={openEmotePicker}
                            restrictPosting={restrictPosting}
                            restrictCommenting={restrictCommenting}
                            restrictLiking={restrictLiking}
                            restrictionExpiry={restrictionExpiry}
                            isGuest={isGuest}
                            previewMode={previewMode}
                            isNarrowColumn={isNarrowColumn}
                            cloudItemsMap={cloudItemsMap}
                            cloudItemsMapReady={cloudItemsMapReady}
                        />
                    ))
                )}
                {postsHasMore && (
                    <button
                        type="button"
                        onClick={() => fetchPosts('append', postsOffset + POSTS_PAGE_SIZE)}
                        disabled={postsLoadingMore}
                        className="w-full py-2 text-sm font-bold text-coal/60 hover:text-coal hover:bg-black/5 border-t border-black/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {postsLoadingMore ? "Loading…" : "Load more posts…"}
                    </button>
                )}
            </div>

            {confirmState && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/20 p-3">
                    <div className="w-full max-w-sm border border-black/40 bg-offwhite rounded shadow-md overflow-hidden">
                        <div className="px-3 py-2 border-b border-black/20 font-bold text-coal">{confirmState.title}</div>
                        <div className="p-3 text-sm text-coal">{confirmState.message}</div>
                        <div className="p-2 border-t border-black/20 flex justify-end gap-2 bg-black/5">
                            <button type="button" onClick={closeConfirm} className="px-3 py-1 border border-black/40 bg-offwhite text-coal text-sm rounded hover:bg-black/5">No</button>
                            <button type="button" onClick={runConfirm} className="px-3 py-1 border border-black/40 bg-gold text-coal text-sm rounded hover:brightness-95">Yes</button>
                        </div>
                    </div>
                </div>
            )}

            <ExternalLinkModal
                url={externalLinkUrl}
                onClose={closeExternalLink}
                onContinue={continueExternalLink}
            />

            <FranklyCloudModal
                isOpen={isFranklyCloudOpen}
                onClose={() => setIsFranklyCloudOpen(false)}
                userData={userData}
                cloudUploadMode={cloudUploadMode || 'all'}
                onInsert={handleCloudInsert}
            />
            {editorLightboxItem && (
                <ImageLightbox
                    src={editorLightboxItem.src}
                    alt={editorLightboxItem.name || ''}
                    onClose={() => setEditorLightboxItem(null)}
                />
            )}
        </div>
    );
};

// ── Profile page ──────────────────────────────────────────────────────────────

function Profile({ userData, cloudUploadMode }) {
    const { username } = useParams();
    const navigate = useNavigate();

    const [emoteCtx, setEmoteCtx] = useState({ packs: [], emotesByPack: {} });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const ctx = await loadEmoteContext();
                if (!cancelled) setEmoteCtx(ctx);
            } catch (_) {}
        })();
        return () => { cancelled = true; };
    }, []);


    const [isEmotePickerOpen, setIsEmotePickerOpen] = useState(false);
    const emoteInsertRef = useRef(null);

    const openEmotePicker = useCallback((insertFn) => {
        emoteInsertRef.current = insertFn;
        setIsEmotePickerOpen(true);
    }, []);

    const closeEmotePicker = useCallback(() => setIsEmotePickerOpen(false), []);

    const handleEmoteSelected = useCallback((token) => {
        if (typeof emoteInsertRef.current === "function") {
            emoteInsertRef.current(String(token || ""));
        }
        setIsEmotePickerOpen(false);
    }, []);

    const profileUsername = useMemo(() => String(username || "").trim(), [username]);

    // Latest Habbstagram posts via our API (it holds the partner key). Stays null
    // when they have none, which keeps the box off the profile entirely.
    const [habbstagram, setHabbstagram] = useState(null);
    useEffect(() => {
        setHabbstagram(null);
        if (!profileUsername) return undefined;
        let cancelled = false;
        fetch(`${process.env.REACT_APP_API_URL}habbstagram/${encodeURIComponent(profileUsername)}/posts`)
            .then((res) => (res.ok ? res.json() : null))
            .then((body) => { if (!cancelled && body?.posts?.length) setHabbstagram(body); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [profileUsername]);
    const isLoggedIn = !!userData;
    const isAdmin = Boolean(userData?.hasAdminRole);
    const isStaff = Boolean(userData?.hasStaffRole);
    const [ownedSkinIds, setOwnedSkinIds] = useState([]);


    const currentUsername = String(
        userData?.username || userData?.habbo || userData?.habboName || userData?.name || ""
    ).trim();

    const currentUserId = String(
        userData?.id ?? userData?.ID ?? userData?.userId ?? userData?.userID ?? userData?.UserID ?? userData?.user_id ?? ""
    );

    const isOwnProfile = Boolean(
        isLoggedIn &&
        profileUsername &&
        currentUsername &&
        profileUsername.toLowerCase() === currentUsername.toLowerCase()
    );

    const isSteve =
        ["steve", "rhyss"].includes(String(currentUsername || "").toLowerCase());

    // ── Aggregate page-load state ─────────────────────────────────────────────
    const [pageLayoutLoading, setPageLayoutLoading] = React.useState(true);
    const [pageInitLoading, setPageInitLoading] = React.useState(true);
    const [pageInitData, setPageInitData] = React.useState(null);
    // ─────────────────────────────────────────────────────────────────────────

    const [activeRestrictions, setActiveRestrictions] = React.useState(null);
    const [activeBan, setActiveBan] = React.useState(null);
    const [viewedUserBan, setViewedUserBan] = React.useState(null);
    const [viewerBanChecked, setViewerBanChecked] = React.useState(false);
    const [viewedBanChecked, setViewedBanChecked] = React.useState(false);

    const habboName = useMemo(() => {
        const fallback = isOwnProfile ? (userData?.username || userData?.name) : profileUsername;
        return String(userData?.habbo || userData?.habboName || fallback || profileUsername || "").trim();
    }, [userData, isOwnProfile, profileUsername]);

    const PROFILE_CUSTOMISATION_TYPE = "customisation";

    const [isEditing, setIsEditing] = useState(false);
    const [boxVisibilityOpen, setBoxVisibilityOpen] = useState(false);
    const [previewAsGuest, setPreviewAsGuest] = useState(false);
    const [bio, setBio] = useState("");

    const bioHtml = React.useMemo(() => {
        const t = String(bio || '').trim();
        if (t) return renderEmotesInHtmlMemo(decodeEntitiesToEmojis(bio), emoteCtx);
        return `${profileUsername} has nothing to say!`;
    }, [bio, emoteCtx, profileUsername]);
    const bioHtmlObj = React.useMemo(() => ({ __html: bioHtml }), [bioHtml]);


    const [editingPronouns, setEditingPronouns] = useState(false);
    const [pronounPart1, setPronounPart1] = useState("");
    const [pronounPart2, setPronounPart2] = useState("");
    const [pronounCustom1, setPronounCustom1] = useState("");
    const [pronounCustom2, setPronounCustom2] = useState("");
    const [relationshipLoading, setRelationshipLoading] = useState(true);
    const [relationship, setRelationship] = useState(null);
    const [friendActionLoading, setFriendActionLoading] = useState(false);
    const [blockActionLoading, setBlockActionLoading] = useState(false);
    const [customisation, setCustomisation] = useState({});
    const [customisationLoaded, setCustomisationLoaded] = useState(false);
    const [profileMood, setProfileMood] = useState('');
    const [pinnedBadgeID, setPinnedBadgeID] = useState(null);
    const [pinnedBadgeUploaded, setPinnedBadgeUploaded] = useState(false);

    // ─── Circle Background Modal ──────────────────────────────────────────────
    const [circleBgModalOpen, setCircleBgModalOpen] = useState(false);
    const [myCircleBgs, setMyCircleBgs] = useState([]);
    const [myCircleBgsLoaded, setMyCircleBgsLoaded] = useState(false);
    const [circleBgDraftColor, setCircleBgDraftColor] = useState("");
    const [circleBgDraftImage, setCircleBgDraftImage] = useState(null);
    const [circleBgPreviewTab, setCircleBgPreviewTab] = useState('bio');
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Profile visibility ───────────────────────────────────────────────────
    const debugGuestParam = process.env.NODE_ENV === 'development'
        && new URLSearchParams(window.location.search).get('debugGuest') === '1';
    const guestMode = debugGuestParam || !isLoggedIn;
    const effectiveIsOwnProfile = isOwnProfile && !previewAsGuest;
    const effectiveIsEditing = isEditing && !previewAsGuest;

    const profileVisibility = customisation?.profileVisibility ?? 'private';
    const profileIsPublic = profileVisibility === 'public';
    // ─────────────────────────────────────────────────────────────────────────
    const [originalCustomisation, setOriginalCustomisation] = useState(null);
    const [achievementsLoading, setAchievementsLoading] = useState(true);
    const [achievementStatsId, setAchievementStatsId] = useState(null);
    const [achievements, setAchievements] = useState([]);

    const FRIENDS_PAGE_SIZE = 20;
    const [friendsLoading, setFriendsLoading] = useState(true);
    const [friends, setFriends] = useState([]);
    const [friendsTotal, setFriendsTotal] = useState(0);
    const [friendsHasMore, setFriendsHasMore] = useState(false);
    const [friendsOffset, setFriendsOffset] = useState(0);
    const [friendsLoadingMore, setFriendsLoadingMore] = useState(false);

    const [showAllFriends, setShowAllFriends] = useState(false);
    const [friendsTab, setFriendsTab] = useState('top');
    const [topFriends, setTopFriends] = useState([]);
    const [topFriendsLoading, setTopFriendsLoading] = useState(true);
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [suggestionsLoaded, setSuggestionsLoaded] = useState(false);
    const [magicWardrobeLoading, setMagicWardrobeLoading] = useState(false);
    const [editingHeader, setEditingHeader] = useState(null);
    const [headerTitles, setHeaderTitles] = useState({});
    const [boxLayout, setBoxLayout] = useState({ left: [], right: [] });
    const [draftLayout, setDraftLayout] = useState(null);
    const rafRef = React.useRef(0);
    const pendingRef = React.useRef(null);
    const aggregateSetRelationshipRef = useRef(null);
    const aggregateSetViewedBanRef = useRef(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const [activeId, setActiveId] = useState(null);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 640);
    const [youtubeUrlInput, setYoutubeUrlInput] = useState("");
    const [currentUserFriends, setCurrentUserFriends] = useState([]);

    const [galleryCloudItemsMap, setGalleryCloudItemsMap] = useState({});
    const [galleryCloudItemsMapReady, setGalleryCloudItemsMapReady] = useState(false);

    const [bannerUrl, setBannerUrl] = useState(null);
    const [pendingBannerDataUrl, setPendingBannerDataUrl] = useState(null);
    const [originalBannerUrl, setOriginalBannerUrl] = useState(null);
    const [showBannerModal, setShowBannerModal] = useState(false);
    const [imagerScenes, setImagerScenes] = useState([]);
    const [isFetchingScenes, setIsFetchingScenes] = useState(false);
    const [selectedSceneId, setSelectedSceneId] = useState(null);
    const [scenePreviews, setScenePreviews] = useState(new Map());
    const sceneCanvasesRef = useRef(new Map());

    // Profile background image (separate from pageBg color)
    const [profileBgImageUrl, setProfileBgImageUrl] = useState(null);
    const [pendingBgImageUrl, setPendingBgImageUrl] = useState(undefined); // undefined=unchanged, null=clear, string=new
    const [originalBgImageUrl, setOriginalBgImageUrl] = useState(null);
    const [showBgModal, setShowBgModal] = useState(false);
    const [tiledScenes, setTiledScenes] = useState([]);
    const [isFetchingTiledScenes, setIsFetchingTiledScenes] = useState(false);
    const [selectedBgSceneId, setSelectedBgSceneId] = useState(null);
    const [tiledScenePreviews, setTiledScenePreviews] = useState(new Map());
    const tiledSceneCanvasesRef = useRef(new Map());
    const [bgModalMode, setBgModalMode] = useState('presetgrid'); // 'presetgrid' | 'scene'
    const [bgModalDraftColor, setBgModalDraftColor] = useState('#ffffff');
    const [bgModalPresetId, setBgModalPresetId] = useState(null); // null = colour selected, string = preset id

    const API_BASE = (window.__API_BASE__ || process.env.REACT_APP_API_URL).trim();
    const apiJoin = (base, path) => (base.endsWith("/") ? base : base + "/") + path.replace(/^\//, "");

    const prox = (u) => {
        const s0 = String(u || "").trim();
        if (!s0) return "";
        if (s0.startsWith("data:")) return s0;
        if (s0.startsWith("/")) return s0;
        if (s0.includes("/api/imager/proxy?url=")) return s0;

        const s = /^https?:\/\//i.test(s0) ? s0 : `https://${s0}`;
        return apiJoin(API_BASE, `imager/proxy?url=${encodeURIComponent(s)}`);
    };



    const BANNER_W = 332;
    const BANNER_H = 160;

    const [sceneCrops, setSceneCrops] = useState({});
    const [cropEditorScene, setCropEditorScene] = useState(null);
    const cropDisplayScaleRef = useRef(1);

    const cropViewportRef = useRef(null);
    const cropCanvasRef = useRef(null);
    const cropDragRef = useRef(null);

    const clampInt = (v, a, b) => Math.max(a, Math.min(b, Math.round(v)));

    const getSceneCrop = (id) => sceneCrops[id] || { x: 0, y: 0 };
    const setSceneCrop = (id, patch) =>
        setSceneCrops((prev) => ({ ...prev, [id]: { ...(prev[id] || { x: 0, y: 0 }), ...patch } }));

    const drawCropEditorCanvas = useCallback((scene) => {
        const el = cropCanvasRef.current;
        const src = sceneCanvasesRef.current.get(scene?.id);
        if (!el || !src) return;
        el.width  = src.width;
        el.height = src.height;
        const ctx = el.getContext("2d");
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(src, 0, 0);
    }, []);

    useEffect(() => {
        if (!cropEditorScene) return;
        requestAnimationFrame(() => drawCropEditorCanvas(cropEditorScene));
    }, [cropEditorScene, drawCropEditorCanvas]);

    const speechPartsBase = {
        frnt: `${process.env.PUBLIC_URL}/img/spbb_frnt.png`,
        mid: `${process.env.PUBLIC_URL}/img/spbb_mid.png`,
        end: `${process.env.PUBLIC_URL}/img/spbb_end.png`
    };

    // Renders a scene to a canvas, caches it, and sets a WebP preview URL in state.
    // Safe to call multiple times for the same scene — subsequent calls are no-ops.
    const generateSceneCanvas = async (scene) => {
        if (!scene?.id) return;
        if (sceneCanvasesRef.current.has(scene.id)) return; // already done or in progress
        sceneCanvasesRef.current.set(scene.id, null); // mark in-progress
        try {
            const canvas = await renderSceneToCanvas(scene, {
                proxyUrl: prox,
                publicUrl: process.env.PUBLIC_URL,
                speechParts: speechPartsBase,
            });
            sceneCanvasesRef.current.set(scene.id, canvas);
            await new Promise(resolve => {
                canvas.toBlob(blob => {
                    if (blob) {
                        setScenePreviews(prev => new Map(prev).set(scene.id, URL.createObjectURL(blob)));
                    }
                    resolve();
                }, 'image/png');
            });
        } catch {
            sceneCanvasesRef.current.delete(scene.id); // allow retry
        }
    };

    useEffect(() => {
        const checkScreen = () => setIsSmallScreen(window.innerWidth < 640);
        checkScreen();
        window.addEventListener('resize', checkScreen);
        return () => window.removeEventListener('resize', checkScreen);
    }, []);

    useEffect(() => {
        if (!profileUsername) return;
        let cancelled = false;
        setGalleryCloudItemsMap({});
        setGalleryCloudItemsMapReady(false);
        const apiOrigin = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '');
        const resolveUpload = (src) => (!src || src.startsWith('data:') || src.startsWith('http') ? src : `${apiOrigin}${src.startsWith('/') ? '' : '/'}${src}`);
        getFranklyCloudItemsForUser(profileUsername)
            .then(async data => {
                if (cancelled) return;
                if (data?.success && Array.isArray(data.items)) {
                    const map = {};
                    await Promise.all(data.items.map(async item => {
                        const decoded = item.type === 'upload' ? { ...item, src: resolveUpload(item.src) } : await decodeCloudItem(item);
                        map[decoded.id] = decoded;
                    }));
                    if (!cancelled) setGalleryCloudItemsMap(map);
                }
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setGalleryCloudItemsMapReady(true); });
        return () => { cancelled = true; };
    }, [profileUsername]);

    useEffect(() => {
        const onDeleted = (e) => {
            const itemId = e.detail?.itemId;
            if (itemId == null) return;
            setGalleryCloudItemsMap(prev => ({ ...prev, [itemId]: { deleted: true } }));
        };
        window.addEventListener('frankly:cloudImageDeleted', onDeleted);
        return () => window.removeEventListener('frankly:cloudImageDeleted', onDeleted);
    }, []);

    const findColumnOf = (key, layout) => {
        if (layout.left.includes(key)) return "left";
        if (layout.right.includes(key)) return "right";
        return null;
    };

    const [confirmState, setConfirmState] = useState(null);

    const requestConfirm = ({ title, message, onConfirm }) => setConfirmState({ title, message, onConfirm });
    const closeConfirm = () => setConfirmState(null);
    const runConfirm = async () => {
        const fn = confirmState?.onConfirm;
        closeConfirm();
        if (typeof fn === "function") await fn();
    };

    const [reportModalOpen, setReportModalOpen] = useState(false);

    const openReportModal = () => {
        if (!isLoggedIn) { toast.error('You must be logged in to report profiles.'); return; }
        setReportModalOpen(true);
    };

    const runToggle = async () => {
        if (!canShowActions || actionsDisabled || relationshipLoading || blockActionLoading) return;

        setBlockActionLoading(true);
        try {
            if (relationship?.isBlocking) {
                const r = await unblockProfileUser(profileUsername);
                if (r?.success !== true) toast.error("Failed to unblock user.");
            } else {
                const r = await blockProfileUser(profileUsername);
                if (r?.success !== true) toast.error("Failed to block user.");
            }
            aggregateSetRelationshipRef.current = null;
            await fetchRelationship();
        } catch {
            toast.error("Block action failed. Please try again.");
        } finally {
            setBlockActionLoading(false);
        }
    };

    const sanitiseYouTubeId = (value) => { const v = String(value ?? "").trim(); return /^[a-zA-Z0-9_-]{11}$/.test(v) ? v : ""; };

    const youtubeVideoId = sanitiseYouTubeId(customisation?.youtubeVideoId);

    useEffect(() => {
        if (!isEditing) return;
        const id = sanitiseYouTubeId(customisation?.youtubeVideoId);
        if (id) setYoutubeUrlInput(`https://youtu.be/${id}`); else setYoutubeUrlInput("");
    }, [isEditing, customisation?.youtubeVideoId]);

    const scheduleDraft = (updater) => {
        pendingRef.current = updater;
        if (rafRef.current) return;
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = 0;
            const fn = pendingRef.current; pendingRef.current = null;
            setDraftLayout((prev) => fn(prev));
        });
    };

    const moveItem = (layout, activeKey, overId) => {
        const overKey = String(overId);
        const fromCol = findColumnOf(activeKey, layout);
        if (!fromCol) return layout;
        let toCol = overKey === "left-column" ? "left" : overKey === "right-column" ? "right" : findColumnOf(overKey, layout);
        if (!toCol) return layout;
        const withoutFrom = layout[fromCol].filter((k) => k !== activeKey);
        const nextTo = layout[toCol].filter((k) => k !== activeKey);
        const idx = nextTo.indexOf(overKey);
        nextTo.splice(overKey === "left-column" || overKey === "right-column" ? nextTo.length : idx >= 0 ? idx : nextTo.length, 0, activeKey);
        if (fromCol === toCol) return { ...layout, [toCol]: nextTo };
        return { ...layout, [fromCol]: withoutFrom, [toCol]: nextTo };
    };

    const handleDragStart = (event) => { setActiveId(String(event.active.id)); setDraftLayout(boxLayout); };
    const handleDragOver = (event) => {
        const { active, over } = event;
        if (!over) return;
        scheduleDraft((prev) => moveItem(prev || boxLayout, String(active.id), over.id));
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        const finalLayout = draftLayout || boxLayout;
        setActiveId(null); setDraftLayout(null); pendingRef.current = null;
        if (rafRef.current) cancelAnimationFrame(rafRef.current); rafRef.current = 0;
        if (!over) return;
        setBoxLayout(moveItem(finalLayout, String(active.id), over.id));
    };

    const timeAgo = (value) => {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return "";
        const diff = Date.now() - d.getTime();
        const sec = Math.floor(diff / 1000);
        if (sec < 60) return "just now";
        const min = Math.floor(sec / 60);
        if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
        const hr = Math.floor(min / 60);
        if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
        const day = Math.floor(hr / 24);
        if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
        const mo = Math.floor(day / 30);
        if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
        const yr = Math.floor(mo / 12);
        return `${yr} year${yr === 1 ? "" : "s"} ago`;
    };

    const sanitiseHex = (value) => { const v = String(value ?? "").trim(); return /^#[0-9A-Fa-f]{6}$/.test(v) ? v : ""; };
    const encodeEmojisToEntities = (s) => String(s ?? "").replace(/[\u{80}-\u{10FFFF}]/gu, (ch) => `&#${ch.codePointAt(0)};`);
    function decodeEntitiesToEmojis(s) { return String(s ?? "").replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(Number(n))); }
    const sanitiseInput = (value) => String(value ?? "").replace(/\u0000/g, "").replace(/[<>]/g, "").slice(0, 500);
    const safeParsePayload = (payload) => {
        if (!payload) return {};
        if (typeof payload === "string") { try { return JSON.parse(payload) || {}; } catch { return {}; } }
        if (typeof payload === "object") return payload;
        return {};
    };

    const setCustomisationValue = (key, value) => setCustomisation((prev) => ({ ...(prev || {}), [key]: value }));

    const getBoxSkin = (boxKey) => customisation?.boxSkins?.[boxKey] || 'default';
    const setBoxSkin = (boxKey, skinId) => setCustomisation(prev => ({
        ...(prev || {}),
        boxSkins: { ...((prev || {}).boxSkins || {}), [boxKey]: skinId },
    }));

    const setHeaderColour = (boxKey, hex) => {
        const clean = sanitiseHex(hex);
        setCustomisation((prev) => ({ ...(prev || {}), headerColours: { ...((prev || {}).headerColours || {}), [boxKey]: clean } }));
    };

    const getHeaderColour = (boxKey) => sanitiseHex(customisation?.headerColours?.[boxKey]) || "";
    const setHeaderTitle = (boxKey, title) => setHeaderTitles((prev) => ({ ...(prev || {}), [boxKey]: encodeEmojisToEntities(title) }));
    const getHeaderTitle = (boxKey) => decodeEntitiesToEmojis(headerTitles?.[boxKey] || "");

    const galleries = useMemo(() => {
        const g = customisation?.galleries;
        return (g && typeof g === 'object') ? g : {};
    }, [customisation?.galleries]);

    const galleryIdsOrdered = useMemo(() => Object.keys(galleries), [galleries]);

    const updateGallery = (id, patch) => {
        if (!id || !isGalleryKey(id)) return;
        setCustomisation(prev => {
            const existing = (prev?.galleries || {})[id];
            if (!existing) return prev;
            return {
                ...(prev || {}),
                galleries: { ...(prev?.galleries || {}), [id]: { ...existing, ...patch } },
            };
        });
    };

    const addGalleryBox = () => {
        if (galleryIdsOrdered.length >= MAX_GALLERY_BOXES) return null;
        const newId = `${GALLERY_KEY_PREFIX}${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
        const nextNumber = galleryIdsOrdered.length + 1;
        setCustomisation(prev => {
            const prevGalleries = (prev?.galleries && typeof prev.galleries === 'object') ? prev.galleries : {};
            return {
                ...(prev || {}),
                galleries: { ...prevGalleries, [newId]: { images: [], imageMode: 'stretched', showHeader: true } },
            };
        });
        setHeaderTitles(prev => ({ ...(prev || {}), [newId]: `Gallery ${nextNumber}` }));
        setBoxLayout(prev => ({
            left: Array.isArray(prev?.left) ? prev.left : [],
            right: [newId, ...(Array.isArray(prev?.right) ? prev.right : [])],
        }));
        return newId;
    };

    const removeGalleryBox = (id) => {
        if (!id || !isGalleryKey(id)) return;
        setCustomisation(prev => {
            const next = { ...(prev || {}) };
            const { [id]: _omit, ...restGalleries } = (prev?.galleries || {});
            next.galleries = restGalleries;
            if (prev?.headerColours && id in prev.headerColours) {
                const { [id]: _hc, ...rest } = prev.headerColours;
                next.headerColours = rest;
            }
            if (prev?.boxSkins && id in prev.boxSkins) {
                const { [id]: _bs, ...rest } = prev.boxSkins;
                next.boxSkins = rest;
            }
            if (prev?.boxVisibility && id in prev.boxVisibility) {
                const { [id]: _bv, ...rest } = prev.boxVisibility;
                next.boxVisibility = rest;
            }
            return next;
        });
        setHeaderTitles(prev => {
            if (!prev || !(id in prev)) return prev;
            const { [id]: _omit, ...rest } = prev;
            return rest;
        });
        setBoxLayout(prev => ({
            left: (prev?.left || []).filter(k => k !== id),
            right: (prev?.right || []).filter(k => k !== id),
        }));
    };

    const getLuvHeartColour = () =>
        sanitiseHex(customisation?.luvHeartColour) || DEFAULT_LUV_HEART_COLOUR;

    const setLuvHeartColour = (hex) => {
        const clean = sanitiseHex(hex);
        setCustomisationValue("luvHeartColour", clean);
    };

    const getLuvHeartSkin = () => customisation?.luvHeartSkin || 'default';

    const setLuvHeartSkin = (skinId) => setCustomisationValue('luvHeartSkin', skinId);

    // ─── Circle Background Modal handlers ────────────────────────────────────
    const openCircleBgModal = async () => {
        const current = String(customisation?.avatarBg ?? '');
        const isUrl = /^https?:\/\//.test(current) || /^\/img\//.test(current);

        // Select the matching owned background object so the grid highlights it (and its
        // name shows). Falls back to a bare {imageURL} when it isn't in the owned list.
        const selectFromList = (list) => {
            const match = (list || []).find(b => String(b.imageURL) === current);
            setCircleBgDraftImage(match || { imageURL: current });
            setCircleBgDraftColor('');
        };

        if (isUrl) {
            selectFromList(myCircleBgs);
        } else {
            setCircleBgDraftColor(sanitiseHex(current) || '#ffffff');
            setCircleBgDraftImage(null);
        }
        setCircleBgModalOpen(true);
        if (!myCircleBgsLoaded) {
            try {
                const result = await getMyCircleBgs();
                const list = result?.backgrounds || [];
                setMyCircleBgs(list);
                if (isUrl) selectFromList(list);
            } catch {}
            setMyCircleBgsLoaded(true);
        }
    };

    const handleCircleBgConfirm = () => {
        if (circleBgDraftImage) {
            setCustomisationValue('avatarBg', circleBgDraftImage.imageURL);
        } else {
            const clean = sanitiseHex(circleBgDraftColor);
            setCustomisationValue('avatarBg', clean || '');
        }
        setCircleBgModalOpen(false);
    };
    // ─────────────────────────────────────────────────────────────────────────

    const loadTopFriends = async () => {
        if (!profileUsername) return;
        try {
            setTopFriendsLoading(true);
            const r = await getTopProfileFriends(profileUsername);
            if (r?.success && Array.isArray(r.friends)) {
                setTopFriends(r.friends.map(f => ({ ...f, profileBg: f.profileBg || '#5A2929' })));
            } else {
                setTopFriends([]);
            }
        } catch {
            setTopFriends([]);
        } finally {
            setTopFriendsLoading(false);
        }
    };

    const loadSuggestions = async () => {
        if (!isLoggedIn || !isOwnProfile) return;
        try {
            setSuggestionsLoading(true);
            const r = await getProfileFriendSuggestions();
            if (r?.success && Array.isArray(r.suggestions)) {
                setSuggestions(r.suggestions.map(s => ({ ...s, profileBg: s.profileBg || '#5A2929' })));
            } else {
                setSuggestions([]);
            }
        } catch {
            setSuggestions([]);
        } finally {
            setSuggestionsLoading(false);
            setSuggestionsLoaded(true);
        }
    };

    const magicWardrobeLastCallRef = useRef(0);

    const handleMagicWardrobe = async () => {
        if (magicWardrobeLoading) return;

        const now = Date.now();
        if (now - magicWardrobeLastCallRef.current < MAGIC_WARDROBE_COOLDOWN_MS) return;
        magicWardrobeLastCallRef.current = now;

        setMagicWardrobeLoading(true);
        try {
            const r = await getMagicWardrobeProfile();
            if (r?.success && r.habboName) {
                navigate(`/members/profile/${encodeURIComponent(r.habboName)}`);
            } else {
                toast.warning('No profiles available to teleport to!');
            }
        } catch {
            toast.error('Magic Wardrobe failed. Please try again.');
        } finally {
            setMagicWardrobeLoading(false);
        }
    };


    const BOX_REGISTRY = useMemo(() => ([
        {
            key: "achievements",
            label: "Achievements",
            description: "Achievements box",
            defaultColumn: "left",
            render: () => (
                <EditableBox boxKey="achievements" theme="default" header="Achievements" padding="p-0" isEditing={isEditing} isOwnProfile={isOwnProfile} getHeaderColour={getHeaderColour} setHeaderColour={setHeaderColour} getBoxSkin={getBoxSkin} setBoxSkin={setBoxSkin} isStaff={isStaff} ownedSkinIds={ownedSkinIds}>
                    <AchievementsBox
                        profileUsername={profileUsername}
                        achievements={achievements}
                        achievementsLoading={achievementsLoading}
                        achievementStatsId={achievementStatsId}
                        setAchievementStatsId={setAchievementStatsId}
                        headerColour={getHeaderColour("achievements")}
                        timeAgo={timeAgo}
                        loadProfileAchievements={loadProfileAchievements}
                        isOwnProfile={isOwnProfile}
                        pinnedBadgeID={pinnedBadgeID}
                        savePinnedBadge={isOwnProfile ? savePinnedBadge : null}
                    />
                </EditableBox>
            )
        },{
            key: "youtube",
            label: "Check this out!",
            description: "YouTube box",
            defaultColumn: "left",
            render: () => (
                <EditableBox boxKey="youtube" theme="default" header="Check this out!" padding="p-0" isEditing={isEditing} isOwnProfile={isOwnProfile} getHeaderColour={getHeaderColour} setHeaderColour={setHeaderColour} getBoxSkin={getBoxSkin} setBoxSkin={setBoxSkin} isStaff={isStaff} ownedSkinIds={ownedSkinIds}>
                    <YouTubeBox
                        youtubeUrlInput={youtubeUrlInput}
                        setYoutubeUrlInput={setYoutubeUrlInput}
                        youtubeVideoId={youtubeVideoId}
                        effectiveIsEditing={effectiveIsEditing}
                        setCustomisationValue={setCustomisationValue}
                    />
                </EditableBox>
            )
        },
        ...(habbstagram ? [{
            key: "habbstagram",
            label: "Habbstagram",
            description: "Latest Habbstagram posts",
            defaultColumn: "left",
            render: () => (
                <EditableBox boxKey="habbstagram" theme="default" header="Habbstagram" padding="p-0"
                             isEditing={isEditing} isOwnProfile={isOwnProfile}
                             getHeaderColour={getHeaderColour} setHeaderColour={setHeaderColour} getBoxSkin={getBoxSkin} setBoxSkin={setBoxSkin}
                             editingHeader={editingHeader} setEditingHeader={setEditingHeader}
                             getHeaderTitle={getHeaderTitle} setHeaderTitle={setHeaderTitle}
                             isStaff={isStaff} ownedSkinIds={ownedSkinIds}>
                    <HabbstagramBox data={habbstagram} />
                </EditableBox>
            )
        }] : []),
        {
            key: "fancyfont",
            label: "Check this out!",
            description: "Fancy font box",
            defaultColumn: "right",
            render: () => (
                <EditableBox boxKey="fancyfont" theme="default" header="Check this out!" padding="p-0"
                             isEditing={isEditing} isOwnProfile={isOwnProfile}
                             getHeaderColour={getHeaderColour} setHeaderColour={setHeaderColour} getBoxSkin={getBoxSkin} setBoxSkin={setBoxSkin}
                             editingHeader={editingHeader} setEditingHeader={setEditingHeader}
                             getHeaderTitle={getHeaderTitle} setHeaderTitle={setHeaderTitle}
                             isStaff={isStaff} ownedSkinIds={ownedSkinIds}>
                    <FancyFontBox
                        isEditing={isEditing}
                        isOwnProfile={isOwnProfile}
                        customisation={customisation}
                        setCustomisationValue={setCustomisationValue}
                    />
                </EditableBox>
            )
        },
        {
            key: "onMyMind",
            label: "On my mind...",
            description: "Posts box",
            defaultColumn: "right",
            render: (columnId = 'right') => (
                <EditableBox boxKey="onMyMind" theme="default" header="On my mind..." padding="p-0" isEditing={isEditing} isOwnProfile={isOwnProfile} getHeaderColour={getHeaderColour} setHeaderColour={setHeaderColour} getBoxSkin={getBoxSkin} setBoxSkin={setBoxSkin} isStaff={isStaff} ownedSkinIds={ownedSkinIds}>
                    <OnMyMindContent
                        profileUsername={profileUsername}
                        isOwnProfile={effectiveIsOwnProfile}
                        userData={userData}
                        timeAgo={timeAgo}
                        headerColour={getHeaderColour("onMyMind")}
                        mentionFriends={currentUserFriends}
                        restrictPosting={guestMode || !!activeRestrictions?.restrictPosting}
                        restrictCommenting={guestMode || !!activeRestrictions?.restrictCommenting}
                        restrictLiking={guestMode || !!activeRestrictions?.restrictLiking}
                        restrictionExpiry={activeRestrictions?.expiresAt ?? null}
                        isSteve={isSteve}
                        isGuest={guestMode}
                        previewMode={previewAsGuest}
                        initialPostsData={pageInitData?.posts}
                        isNarrowColumn={columnId === 'left'}
                        cloudUploadMode={cloudUploadMode}
                    />
                </EditableBox>
            )
        },
        {
            key: "franklyFriends",
            label: "Top Friends!",
            description: "Friends box",
            defaultColumn: "right",
            render: () => {
                const topFriendsOrder = customisation?.topFriendsOrder || [];
                const topFriendIds = new Set(topFriendsOrder.slice(0, 15));
                const isInRightColumn = normalisedLayout.right.includes("franklyFriends");
                const useWideTopFriends = isInRightColumn && !isSmallScreen;

                const handleTopFriendToggle = (friendId) => {
                    setCustomisation((prev) => {
                        const currentOrder = prev?.topFriendsOrder || [];
                        const nextOrder = currentOrder.includes(friendId)
                            ? currentOrder.filter(id => id !== friendId)
                            : [...currentOrder, friendId];
                        return { ...(prev || {}), topFriendsOrder: nextOrder };
                    });
                    setTopFriends((prev) => {
                        const isRemoving = prev.some(f => f.userID === friendId);
                        if (isRemoving) return prev.filter(f => f.userID !== friendId);
                        if (prev.length >= 15) return prev;
                        const source = friends.find(f => f.userID === friendId);
                        if (!source) return prev;
                        return [...prev, { userID: source.userID, habboName: source.habboName, profileBg: source.profileBg || '#5A2929' }];
                    });
                };

                const handleGridDragEnd = (event) => {
                    const { active, over } = event;
                    if (!over || active.id === over.id) return;
                    setCustomisation((prev) => {
                        const currentOrder = [...(prev?.topFriendsOrder || [])];
                        const oldIndex = currentOrder.indexOf(active.id);
                        const newIndex = currentOrder.indexOf(over.id);
                        if (oldIndex === -1 || newIndex === -1) return prev;
                        return { ...(prev || {}), topFriendsOrder: arrayMove(currentOrder, oldIndex, newIndex) };
                    });
                    setTopFriends((prev) => {
                        const oldIndex = prev.findIndex(f => f.userID === active.id);
                        const newIndex = prev.findIndex(f => f.userID === over.id);
                        if (oldIndex === -1 || newIndex === -1) return prev;
                        return arrayMove([...prev], oldIndex, newIndex);
                    });
                };

                const starBtn = (has, onToggle, disabled) => (
                    <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }} disabled={disabled} className={`absolute top-1 right-1 z-10 w-5 h-5 flex items-center justify-center rounded transition-colors ${has ? 'bg-gold text-coal' : 'bg-offwhite/80'}`}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill={has ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                    </button>
                );

                const TAB_BASE = "px-2 py-1.5 text-xs font-bold border-b-2 transition-colors whitespace-nowrap";
                const TAB_ACTIVE = "border-coal text-coal";
                const TAB_INACTIVE = "border-transparent text-coal/50 hover:text-coal/80";

                const tabs = [
                    { id: 'top', label: 'Top Friends' },
                    { id: 'all', label: 'All Friends' },
                    ...(isOwnProfile ? [
                        { id: 'suggestions', label: 'Suggestions' },
                        { id: 'wardrobe', label: 'Magic Wardrobe' },
                    ] : []),
                ];

                const handleTabChange = (tabId) => {
                    setFriendsTab(tabId);
                    if (tabId === 'all' && friends.length === 0 && !friendsLoading) {
                        loadProfileFriends("reset");
                    }
                    if (tabId === 'suggestions' && !suggestionsLoaded && !suggestionsLoading) {
                        loadSuggestions();
                    }
                };

                const renderTopFriendsGrid = () => {
                    if (topFriendsLoading) return <div className="text-coal/70 p-3">Loading top friends...</div>;
                    if (topFriends.length === 0) return <div className="text-coal/80 p-3">{effectiveIsOwnProfile ? 'Star friends (in edit mode) to add them to your top friends!' : `${profileUsername} has no top friends!`}</div>;

                    return effectiveIsEditing && effectiveIsOwnProfile ? (
                        <DndContext sensors={sensors} collisionDetection={rectIntersection} onDragEnd={handleGridDragEnd}>
                            <SortableContext items={topFriends.map(f => f.userID)} strategy={rectSortingStrategy}>
                                <div className="grid grid-cols-5 gap-px bg-black/20 p-px">
                                    {topFriends.map((friend) => (
                                        <SortableFriendItem key={friend.userID} friend={friend} isEditing={effectiveIsEditing} isOwnProfile={effectiveIsOwnProfile} avatarSize={useWideTopFriends ? "b" : "a"} onClick={() => !effectiveIsEditing && navigate(`/members/profile/${encodeURIComponent(friend.habboName)}`)} />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    ) : (
                        <div className="grid grid-cols-5 gap-px bg-black/20 p-px">
                            {topFriends.map((friend) => {
                                const bg = friend.profileBg || '#5A2929';
                                const hexBg = safeHexOrFallback(bg);
                                const textColor = luminance(hexBg) < 0.16 ? '#f5f5f0' : '#222222';
                                return (
                                    <div key={friend.userID} className="flex flex-col items-center justify-center relative cursor-pointer hover:brightness-95 overflow-hidden" style={{ ...getAvatarBgStyle(bg), aspectRatio: useWideTopFriends ? '1 / 1' : '3 / 4' }} onClick={() => navigate(`/members/profile/${encodeURIComponent(friend.habboName)}`)}>
                                        <div className="relative pointer-events-none top-[-8px]"><HabboImage habbo={friend.habboName} hotel="COM" size={useWideTopFriends ? "b" : "a"} direction={2} head_direction={3} gesture="sml" style={{ imageRendering: "pixelated", display: "block" }} /></div>
                                        <div className="absolute bottom-1 left-0 right-0 font-['Jersey_10'] text-base text-center leading-none truncate px-0.5 pointer-events-none tracking-widest" style={{ color: textColor }}>{friend.habboName}</div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                };

                const renderAllFriendsGrid = () => {
                    if (friendsLoading) return <div className="text-coal/70 p-3">Loading friends...</div>;
                    if (friends.length === 0) return <div className="text-coal/80 p-3">{profileUsername} has no friends!</div>;

                    return (
                        <>
                            <div className="grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-px bg-black/20 p-px">
                                {friends.map((friend) => (
                                    <div key={friend.userID} className="bg-offwhite p-2 flex flex-col items-center gap-1 relative group cursor-pointer hover:brightness-95" onClick={() => !isEditing && navigate(`/members/profile/${encodeURIComponent(friend.habboName)}`)}>
                                        {effectiveIsEditing && effectiveIsOwnProfile && starBtn(topFriendIds.has(friend.userID), () => handleTopFriendToggle(friend.userID), !topFriendIds.has(friend.userID) && topFriends.length >= 15)}
                                        <div className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden" style={getAvatarBgStyle(friend.profileBg || '#5A2929')}>
                                            <HabboImage habbo={friend.habboName} hotel="COM" size="a" direction={2} head_direction={3} gesture="sml" />
                                        </div>
                                        <div className="font-['Jersey_10'] text-lg text-coal text-center leading-none truncate w-full px-1 tracking-widest">{friend.habboName}</div>
                                    </div>
                                ))}
                            </div>
                            {friendsHasMore && (
                                <div className="p-2 flex justify-center border-t border-black/10">
                                    <button
                                        type="button"
                                        disabled={friendsLoadingMore}
                                        onClick={() => loadProfileFriends("append")}
                                        className="px-3 py-1 text-xs border border-black/30 bg-offwhite text-coal rounded hover:bg-black/10 transition-colors disabled:opacity-60"
                                    >
                                        {friendsLoadingMore ? "Loading..." : "Show more"}
                                    </button>
                                </div>
                            )}
                        </>
                    );
                };

                const renderSuggestions = () => {
                    if (suggestionsLoading) return <div className="text-coal/70 p-3">Loading suggestions...</div>;
                    if (suggestions.length === 0) return <div className="text-coal/80 p-3">No suggestions available right now.</div>;

                    return (
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-px bg-black/20 p-px">
                            {suggestions.map((s) => (
                                <div key={s.userID} className="bg-offwhite p-2 flex flex-col items-center gap-1 cursor-pointer hover:brightness-95" onClick={() => navigate(`/members/profile/${encodeURIComponent(s.habboName)}`)}>
                                    <div className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden" style={getAvatarBgStyle(s.profileBg || '#5A2929')}>
                                        <HabboImage habbo={s.habboName} hotel="COM" size="a" direction={2} head_direction={3} gesture="sml" />
                                    </div>
                                    <div className="font-['Jersey_10'] text-lg text-coal text-center leading-none truncate w-full px-1 tracking-widest">{s.habboName}</div>
                                    {s.mutualCount > 0 && (
                                        <div className="text-[10px] text-coal/50 leading-none">{s.mutualCount} mutual</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                };

                const renderMagicWardrobe = () => (
                    <div className="p-6 flex flex-row items-center justify-center gap-6">
                        <img src={`${process.env.PUBLIC_URL}/img/magicwardrobe.png`} alt="Magic Wardrobe" style={{ imageRendering: 'pixelated' }} className="shrink-0 self-center" />
                        <div className="flex flex-col gap-4">
                            <div className="font-['Jersey_10'] text-2xl text-coal leading-tight tracking-widest">
                                ✨ Magic Wardrobe ✨
                            </div>
                            <div className="text-sm text-coal/70">
                                Teleport to a random profile you haven&apos;t visited!
                            </div>
                            <button
                                type="button"
                                onClick={handleMagicWardrobe}
                                disabled={magicWardrobeLoading}
                                className="px-6 py-3 border-2 border-black bg-gold text-coal font-['Jersey_10'] text-xl uppercase rounded hover:brightness-95 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed tracking-widest"
                                style={{ boxShadow: '0 4px 0 0 rgba(0,0,0,0.3)' }}
                            >
                                {magicWardrobeLoading ? 'Teleporting...' : 'Teleport!'}
                            </button>
                        </div>
                    </div>
                );

                return (
                    <EditableBox boxKey="franklyFriends" theme="default" header="Top Friends!" padding="p-0" isEditing={isEditing} isOwnProfile={isOwnProfile} getHeaderColour={getHeaderColour} setHeaderColour={setHeaderColour} getBoxSkin={getBoxSkin} setBoxSkin={setBoxSkin} isStaff={isStaff} ownedSkinIds={ownedSkinIds}>
                        <div className="w-full">
                            <>
                                <div className="flex items-center border-b border-black/20 bg-black/5 overflow-x-auto">
                                    {tabs.map(tab => (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            onClick={() => handleTabChange(tab.id)}
                                            className={`${TAB_BASE} ${friendsTab === tab.id ? TAB_ACTIVE : TAB_INACTIVE}`}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                    {friendsTab === 'all' && (
                                        <span className="ml-auto pr-2 text-[10px] text-coal/40 shrink-0">
                                            {friendsTotal || friends.length} total
                                        </span>
                                    )}
                                </div>
                                <div className="habbo-scroll overflow-y-auto" style={{ maxHeight: friendsTab === 'top' ? 510 : 340 }}>
                                    {friendsTab === 'top' && renderTopFriendsGrid()}
                                    {friendsTab === 'all' && renderAllFriendsGrid()}
                                    {friendsTab === 'suggestions' && renderSuggestions()}
                                    {friendsTab === 'wardrobe' && renderMagicWardrobe()}
                                </div>
                            </>
                        </div>
                    </EditableBox>
                );
            }
        },
        {
            key: "luv",
            label: "Luv",
            description: "Luv box",
            defaultColumn: "left",
            render: () => (
                <LuvBox
                    profileUsername={profileUsername}
                    isOwnProfile={effectiveIsOwnProfile}
                    isLoggedIn={isLoggedIn}
                    isGuest={guestMode}
                    currentUsername={currentUsername}
                    currentUserId={currentUserId}
                    isEditing={isEditing}
                    isOwnProfileBool={effectiveIsOwnProfile}
                    getHeaderColour={getHeaderColour}
                    setHeaderColour={setHeaderColour}
                    getBoxSkin={getBoxSkin}
                    setBoxSkin={setBoxSkin}
                    editingHeader={editingHeader}
                    setEditingHeader={setEditingHeader}
                    headerTitle={getHeaderTitle("luv")}
                    setHeaderTitle={setHeaderTitle}
                    restrictLiking={guestMode || !!activeRestrictions?.restrictLiking}
                    restrictionExpiry={activeRestrictions?.expiresAt ?? null}
                    luvHeartColour={getLuvHeartColour()}
                    setLuvHeartColour={setLuvHeartColour}
                    isSteve={isSteve}
                    isStaff={isStaff}
                    ownedSkinIds={ownedSkinIds}
                    luvHeartSkin={getLuvHeartSkin()}
                    setLuvHeartSkin={setLuvHeartSkin}
                    initialLuvTotal={pageInitData?.luvSummary?.total}
                    initialLuvRecentGiven={pageInitData?.luvRecentGiven}
                />
            )
        },
        {
            key: "status",
            label: "Status",
            description: "Status box",
            defaultColumn: "right",
            render: () => (
                <EditableBox boxKey="status" theme="default" header="Status" padding="p-0" isEditing={isEditing} isOwnProfile={isOwnProfile} getHeaderColour={getHeaderColour} setHeaderColour={setHeaderColour} getBoxSkin={getBoxSkin} setBoxSkin={setBoxSkin} isStaff={isStaff} ownedSkinIds={ownedSkinIds}>
                    <StatusBox
                        profileUsername={profileUsername}
                        isOwnProfile={effectiveIsOwnProfile}
                        isLoggedIn={isLoggedIn}
                        currentUsername={currentUsername}
                        isGuest={guestMode}
                        headerColour={getHeaderColour("status")}
                        initialStatus={pageInitData?.status}
                        initialStatusHistory={pageInitData?.statusHistory}
                        initialCommunityStatuses={pageInitData?.communityStatuses || []}
                    />
                </EditableBox>
            )
        },
        ...galleryIdsOrdered.map((id, i) => {
            const g = galleries[id] || { images: [], imageMode: 'stretched', showHeader: true };
            const defaultTitle = `Gallery ${i + 1}`;
            const showHeaderConfig = g.showHeader !== false;
            return {
                key: id,
                label: defaultTitle,
                description: 'Gallery box',
                defaultColumn: 'right',
                isGallery: true,
                render: () => {
                    const wrapped = showHeaderConfig || effectiveIsEditing;
                    if (wrapped) {
                        return (
                            <EditableBox
                                boxKey={id}
                                theme="default"
                                header={defaultTitle}
                                padding="p-0"
                                isEditing={isEditing}
                                isOwnProfile={isOwnProfile}
                                getHeaderColour={getHeaderColour}
                                setHeaderColour={setHeaderColour}
                                getBoxSkin={getBoxSkin}
                                setBoxSkin={setBoxSkin}
                                isStaff={isStaff}
                                ownedSkinIds={ownedSkinIds}
                            >
                                <GalleryBox
                                    gallery={g}
                                    galleryId={id}
                                    isEditing={effectiveIsEditing}
                                    isOwnProfile={effectiveIsOwnProfile}
                                    onUpdate={(patch) => updateGallery(id, patch)}
                                    cloudItemsMap={galleryCloudItemsMap}
                                    cloudItemsMapReady={galleryCloudItemsMapReady}
                                    userData={userData}
                                    cloudUploadMode={cloudUploadMode}
                                />
                            </EditableBox>
                        );
                    }
                    return (
                        <GalleryBox
                            gallery={g}
                            galleryId={id}
                            isEditing={false}
                            isOwnProfile={false}
                            bare
                            cloudItemsMap={galleryCloudItemsMap}
                            cloudItemsMapReady={galleryCloudItemsMapReady}
                        />
                    );
                },
            };
        }),
    ]), [isEditing, isOwnProfile, effectiveIsOwnProfile, effectiveIsEditing, previewAsGuest, achievementsLoading, achievements, achievementStatsId, profileUsername, customisation, friendsLoading, friends, topFriends, topFriendsLoading, friendsTab, suggestions, suggestionsLoading, isSmallScreen, youtubeUrlInput, currentUsername, currentUserId, isLoggedIn, editingHeader, activeRestrictions, magicWardrobeLoading, suggestionsLoaded, guestMode, pageInitData, ownedSkinIds, pinnedBadgeID, galleryIdsOrdered, galleries, galleryCloudItemsMap, galleryCloudItemsMapReady, userData, cloudUploadMode, habbstagram]);

    const RCA_BADGE_ID = "RCA";
    const rcaAwardAttemptedRef = useRef(false);
    const PRF_BADGE_ID = "PRF";
    const prfAwardAttemptedRef = useRef(false);
    const TFT_BADGE_ID = "TFT";
    const tftAwardAttemptedRef = useRef(false);
    const hasAnyPostRef = useRef(undefined);
    const hasAnyPostPromiseRef = useRef(null);

    useEffect(() => {
        if (typeof hasAnyPostRef.current === "boolean") return;
        if (hasAnyPostPromiseRef.current) return;

        hasAnyPostPromiseRef.current = (async () => {
            try {
                const postsRes = await listProfilePosts(profileUsername, 1, 0);
                const v = !!(postsRes?.success && Array.isArray(postsRes.posts) && postsRes.posts.length > 0);
                hasAnyPostRef.current = v;
                return v;
            } catch {
                hasAnyPostRef.current = false;
                return false;
            }
        })().finally(() => {
            hasAnyPostPromiseRef.current = null;
        });
    }, [profileUsername]);

    const runPostSaveProfileBadgeChecks = React.useCallback(async () => {
        if (!isOwnProfile) return;
        if (!isLoggedIn) return;
        if (!profileUsername) return;

        const headerColours = customisation?.headerColours || {};

        if (!prfAwardAttemptedRef.current) {
            const hasPrfPink = Object.values(headerColours).some(v => String(v || "").trim().toLowerCase() === "#ffb6de");
            if (hasPrfPink) {
                const alreadyHasPrf = Array.isArray(achievements)
                    && achievements.some(a => String(a.badgeID || a.BadgeID || "").toUpperCase() === PRF_BADGE_ID);

                prfAwardAttemptedRef.current = true;

                if (!alreadyHasPrf) {
                    const awardRes = await awardOwnProfileAchievement(PRF_BADGE_ID);
                    if (awardRes?.success && awardRes?.awarded) {
                        toast.success(
                            'Achievement unlocked: “Frankly Perfect!”.\n (You may need to save and refresh to see it.)',
                            { autoClose: 10000 }
                        );

                        clearCachedData(`achievements_${profileUsername.toLowerCase()}`);
                        await loadProfileAchievements();
                    }
                }
            }
        }

        if (!tftAwardAttemptedRef.current && customisation?.hasTiledBg === true) {
            const alreadyHasTft = Array.isArray(achievements)
                && achievements.some(a => String(a.badgeID || a.BadgeID || "").toUpperCase() === TFT_BADGE_ID);

            tftAwardAttemptedRef.current = true;

            if (!alreadyHasTft) {
                const awardRes = await awardOwnProfileAchievement(TFT_BADGE_ID);
                if (awardRes?.success && awardRes?.awarded) {
                    clearCachedData(`achievements_${profileUsername.toLowerCase()}`);
                    await loadProfileAchievements();
                }
            }
        }

        if (rcaAwardAttemptedRef.current) return;

        const alreadyHasRca = Array.isArray(achievements)
            && achievements.some(a => String(a.badgeID || a.BadgeID || "").toUpperCase() === RCA_BADGE_ID);

        if (alreadyHasRca) {
            rcaAwardAttemptedRef.current = true;
            return;
        }

        const hasAnyPost = hasAnyPostRef.current === true;
        if (!hasAnyPost) return;

        const boxKeys = Array.isArray(BOX_REGISTRY) ? BOX_REGISTRY.map(b => b.key).filter(Boolean) : [];

        const hasChangedAllHeaders = boxKeys.length > 0 && boxKeys.every(k => {
            const c = headerColours?.[k];
            const t = headerTitles?.[k];
            return (typeof c === "string" && c.trim().length > 0) && (typeof t === "string" && t.trim().length > 0);
        });

        const hasBio = String(bio || "").trim().length > 0;

        const topFriendsCount = Array.isArray(customisation?.topFriendsOrder)
            ? customisation.topFriendsOrder.filter(Boolean).length
            : 0;
        const has3TopFriends = topFriendsCount >= 3;

        const layout = customisation?.boxLayout;
        const layoutBoxKeys = new Set([
            ...(Array.isArray(layout?.left) ? layout.left : []),
            ...(Array.isArray(layout?.right) ? layout.right : []),
        ]);
        const allBoxesVisible = boxKeys.length > 0 && boxKeys.every(k => layoutBoxKeys.has(k));

        const eligible = hasAnyPost && hasBio && has3TopFriends && hasChangedAllHeaders && allBoxesVisible;
        if (!eligible) return;

        rcaAwardAttemptedRef.current = true;

        const awardRes = await awardOwnProfileAchievement(RCA_BADGE_ID);

        if (!awardRes?.success) {
            toast.warning("Could not award achievement right now. Try refreshing.", { autoClose: 7000 });
            return;
        }

        if (awardRes.awarded) {
            toast.success(
                'Achievement unlocked: “RCA”.\n (You may need to refresh to see it.)',
                { autoClose: 10000 }
            );

            clearCachedData(`achievements_${profileUsername.toLowerCase()}`);
            await loadProfileAchievements();
        }
    }, [
        isOwnProfile,
        isLoggedIn,
        profileUsername,
        achievements,
        customisation,
        headerTitles,
        bio,
        BOX_REGISTRY,
        loadProfileAchievements
    ]);

    const DEFAULT_LAYOUT = useMemo(() => ({
        left: BOX_REGISTRY.filter(b => b.defaultColumn === "left").map(b => b.key),
        right: BOX_REGISTRY.filter(b => b.defaultColumn === "right").map(b => b.key),
    }), [BOX_REGISTRY]);

    const normaliseLayout = (layout) => {
        const all = BOX_REGISTRY.map(b => b.key);
        const seen = new Set();
        const clean = (arr) => (Array.isArray(arr) ? arr : []).filter((k) => (all.includes(k) || isGalleryKey(k)) && !seen.has(k) && (seen.add(k), true));
        const left = clean(layout?.left); const right = clean(layout?.right);
        for (const k of all) { if (!seen.has(k)) { const def = BOX_REGISTRY.find(b => b.key === k); (def?.defaultColumn === "right" ? right : left).push(k); } }
        return { left, right };
    };

    const renderLayout = activeId && draftLayout ? draftLayout : boxLayout;
    const normalisedLayout = useMemo(() => normaliseLayout(renderLayout), [renderLayout, BOX_REGISTRY]);

    const renderLabelWithEmotes = (text) => {
        const out = []; const re = /:(\d+):(\d+):/g; let last = 0; let m;
        while ((m = re.exec(String(text || "")))) {
            if (m.index > last) out.push(text.slice(last, m.index));
            const packName = emoteCtx?.packs?.[Number(m[1]) - 1];
            const entry = packName ? emoteCtx?.emotesByPack?.[packName]?.[Number(m[2]) - 1] : null;
            const file = entry ? (typeof entry === "string" ? entry : (entry?.filename || "")) : "";
            if (packName && file) out.push(<EmoteImg key={m.index} packName={packName} filename={file} alt={m[0]} className="inline-emote" style={{ display: "inline-block", verticalAlign: "middle", height: "1em", width: "auto"}} />);//, imageRendering: "pixelated" }} />);
            else out.push(m[0]);
            last = m.index + m[0].length;
        }
        if (last < String(text || "").length) out.push(text.slice(last));
        return out;
    };

    const LOCKED_BOXES = useMemo(() => new Set(['bio', 'onMyMind', 'achievements']), []);
    const SOFT_LOCKED_BOXES = useMemo(() => new Set(['franklyFriends']), []);
    const boxVisibilityMap = useMemo(() => {
        const bv = customisation?.boxVisibility ?? {};
        if (Object.keys(bv).length === 0 && customisation?.hiddenBoxes?.length > 0) {
            return Object.fromEntries(customisation.hiddenBoxes.map(k => [k, 'hidden']));
        }
        return bv;
    }, [customisation?.boxVisibility, customisation?.hiddenBoxes]);
    const hiddenBoxSet = useMemo(() => new Set(Object.entries(boxVisibilityMap).filter(([, v]) => v === 'hidden').map(([k]) => k)), [boxVisibilityMap]);
    const privateBoxSet = useMemo(() => new Set(Object.entries(boxVisibilityMap).filter(([, v]) => v === 'private').map(([k]) => k)), [boxVisibilityMap]);
    const setBoxVisibility = (key, state) => {
        if (LOCKED_BOXES.has(key)) return;
        if (SOFT_LOCKED_BOXES.has(key) && state === 'hidden') return;
        setCustomisation(prev => {
            const bv = { ...(prev?.boxVisibility ?? {}) };
            if (state === 'visible') delete bv[key];
            else bv[key] = state;
            return { ...(prev || {}), boxVisibility: bv };
        });
    };
    const visibleLayout = useMemo(() => {
        const ownerViewing = isOwnProfile && !previewAsGuest;
        const knownGalleryKeys = new Set(galleryIdsOrdered);
        const isOrphanGalleryKey = (k) => isGalleryKey(k) && !knownGalleryKeys.has(k);
        return {
            left: normalisedLayout.left.filter(k => !isOrphanGalleryKey(k) && !hiddenBoxSet.has(k) && (!privateBoxSet.has(k) || ownerViewing)),
            right: normalisedLayout.right.filter(k => !isOrphanGalleryKey(k) && !hiddenBoxSet.has(k) && (!privateBoxSet.has(k) || ownerViewing)),
        };
    }, [normalisedLayout, hiddenBoxSet, privateBoxSet, isOwnProfile, previewAsGuest, galleryIdsOrdered]);

    const stableStringify = (obj) => {
        const seen = new WeakSet();
        const sorter = (value) => {
            if (value && typeof value === "object") {
                if (seen.has(value)) return null; seen.add(value);
                if (Array.isArray(value)) return value.map(sorter);
                const out = {}; Object.keys(value).sort().forEach((k) => { out[k] = sorter(value[k]); }); return out;
            }
            return value;
        };
        return JSON.stringify(sorter(obj ?? {}));
    };

    const sanitiseAvatarBg = (value) => {
        const s = String(value ?? '').trim();
        if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s;
        try { const u = new URL(s); if (u.protocol === 'https:' || u.protocol === 'http:') return s; } catch { /* not a URL */ }
        if (/^\/(?:img|circlebgassets)\/[^\s]+\.(?:png|jpg|jpeg|gif|webp|svg)$/i.test(s)) return s;
        return '';
    };

    const sanitiseGalleries = (raw) => {
        if (!raw || typeof raw !== 'object') return {};
        const out = {};
        let count = 0;
        for (const [id, val] of Object.entries(raw)) {
            if (!isGalleryKey(id) || !val || typeof val !== 'object') continue;
            if (count >= MAX_GALLERY_BOXES) break;
            const rawImages = Array.isArray(val.images) ? val.images : [];
            const images = [];
            const seen = new Set();
            for (const img of rawImages) {
                if (!img || typeof img !== 'object' || img.id == null) continue;
                const key = String(img.id);
                if (seen.has(key)) continue;
                seen.add(key);
                const ref = { id: img.id, type: String(img.type || ''), name: String(img.name || '').slice(0, 200) };
                if (ref.type === 'upload' && typeof img.src === 'string') ref.src = img.src;
                images.push(ref);
                if (images.length >= 10) break;
            }
            out[id] = {
                images,
                imageMode: val.imageMode === 'native' ? 'native' : 'stretched',
                showHeader: val.showHeader !== false,
            };
            count += 1;
        }
        return out;
    };

    const buildCurrentCustomisationPayload = () => {
        const pageBg = sanitiseHex(customisation?.pageBg);
        const avatarBg = sanitiseAvatarBg(customisation?.avatarBg);
        const headerColours = customisation?.headerColours && typeof customisation.headerColours === "object"
            ? Object.fromEntries(Object.entries(customisation.headerColours).map(([k, v]) => [k, sanitiseHex(v)]).filter(([, v]) => v))
            : {};
        const boxSkins = customisation?.boxSkins && typeof customisation.boxSkins === "object"
            ? Object.fromEntries(Object.entries(customisation.boxSkins).filter(([, v]) => v && v !== 'default'))
            : {};
        const topFriendsOrder = Array.isArray(customisation?.topFriendsOrder) ? customisation.topFriendsOrder : [];
        const luvHeartColour = sanitiseHex(customisation?.luvHeartColour);
        const payload = {
            ...(customisation || {}),
            bio: encodeEmojisToEntities(sanitiseInput(stripEmoteSentinels(bio))),
            headerColours,
            boxSkins,
            headerTitles,
            topFriendsOrder,
            youtubeVideoId: sanitiseYouTubeId(customisation?.youtubeVideoId),
            fancyFontText: sanitiseInput(customisation?.fancyFontText ?? "Welcome!"),
            fancyFontType: customisation?.fancyFontType ?? "Battleball",
            boxLayout: normaliseLayout(boxLayout),
            pronouns: sanitiseInput(customisation?.pronouns ?? ""),
            galleries: sanitiseGalleries(customisation?.galleries),
        };
        if (luvHeartColour) payload.luvHeartColour = luvHeartColour;
        else delete payload.luvHeartColour;

        const luvHeartSkin = customisation?.luvHeartSkin && customisation.luvHeartSkin !== 'default'
            ? customisation.luvHeartSkin : null;
        if (luvHeartSkin) payload.luvHeartSkin = luvHeartSkin;
        else delete payload.luvHeartSkin;

        if (pageBg) payload.pageBg = pageBg;
        if (avatarBg) payload.avatarBg = avatarBg;
        const lowerBg = sanitiseHex(customisation?.lowerBg);
        if (lowerBg) payload.lowerBg = lowerBg; else delete payload.lowerBg;
        const badgeBg = sanitiseHex(customisation?.badgeBg);
        if (badgeBg) payload.badgeBg = badgeBg; else delete payload.badgeBg;
        if (!payload.presetBgId) delete payload.presetBgId;
        delete payload.hiddenBoxes;
        return payload;
    };

    const moodDebounceRef = useRef(null);
    const saveMood = (key) => {
        if (key === profileMood) return;
        setProfileMood(key);
        if (moodDebounceRef.current) clearTimeout(moodDebounceRef.current);
        moodDebounceRef.current = setTimeout(async () => {
            try {
                const r = await upsertOwnProfileData('mood', { mood: key });
                if (!r?.success) toast.error('Failed to save mood.');
            } catch (err) {
                toast.error('Failed to save mood.');
            }
        }, 2000);
    };

    const savePinnedBadge = async (badgeID, uploaded) => {
        setPinnedBadgeID(badgeID);
        setPinnedBadgeUploaded(uploaded ?? false);
        try {
            const r = await upsertOwnProfileData('pinnedBadge', { badgeID, uploaded: uploaded ?? false });
            if (r?.success) {
                clearCachedData(`pinnedBadge_${profileUsername.toLowerCase()}`);
            } else {
                toast.error('Failed to save pinned badge.');
            }
        } catch {
            toast.error('Failed to save pinned badge.');
        }
    };

    const applyCustomisationPayload = (payload) => {
        setCustomisation(payload);
        setBoxLayout(normaliseLayout(payload?.boxLayout || DEFAULT_LAYOUT));
        setHeaderTitles(payload?.headerTitles || {});
        setBio(sanitiseInput(decodeEntitiesToEmojis(payload?.bio ?? "")));
        setCustomisationLoaded(true);
    };

    const base64ToUint8 = (b64) => {
        const bin = atob(b64);
        const u8 = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
        return u8;
    };

    const binarySafeB64ToUtf8 = (b64) => decodeURIComponent(escape(atob(b64)));

    const gunzipBase64ToString = async (b64) => {
        try {
            if (typeof DecompressionStream === "undefined") return binarySafeB64ToUtf8(b64);
            const ds = new DecompressionStream("gzip");
            const u8 = base64ToUint8(b64);
            const stream = new Blob([u8]).stream().pipeThrough(ds);
            return await new Response(stream).text();
        } catch {
            return binarySafeB64ToUtf8(b64);
        }
    };

    const loadProfileBanner = async () => {
        if (!profileUsername) return;

        const cacheKey = `banner_${profileUsername.toLowerCase()}`;

        setBannerUrl(null);

        if (!isOwnProfile) {
            const cached = getCachedData(cacheKey);
            if (cached?.url) {
                setBannerUrl(cached.url);
                return;
            }
        }

        try {
            const r = await getProfileData(profileUsername, "banner");

            if (!r?.success) {
                clearCachedData(cacheKey);
                return;
            }

            const payload = safeParsePayload(r?.data?.jsonPayload);

            if (payload?.url) {
                setBannerUrl(payload.url);
                setCachedData(cacheKey, payload);
            } else {
                clearCachedData(cacheKey);
                setBannerUrl(null);
            }
        } catch {
            setBannerUrl(null);
        }
    };

    const loadProfileBg = async () => {
        if (!profileUsername) return;
        setProfileBgImageUrl(null);
        try {
            const r = await getProfileData(profileUsername, "profile_bg");
            if (r?.success) {
                const payload = safeParsePayload(r?.data?.jsonPayload);
                if (payload?.url) setProfileBgImageUrl(payload.url);
            }
        } catch {}
    };

    const generateTiledSceneCanvas = async (scene) => {
        if (!scene?.id) return;
        if (tiledSceneCanvasesRef.current.has(scene.id)) return;
        tiledSceneCanvasesRef.current.set(scene.id, null);
        try {
            const canvas = await renderSceneToCanvas(scene, {
                proxyUrl: prox,
                publicUrl: process.env.PUBLIC_URL,
                speechParts: speechPartsBase,
            });
            tiledSceneCanvasesRef.current.set(scene.id, canvas);
            await new Promise(resolve => {
                canvas.toBlob(blob => {
                    if (blob) setTiledScenePreviews(prev => new Map(prev).set(scene.id, URL.createObjectURL(blob)));
                    resolve();
                }, 'image/png');
            });
        } catch {
            tiledSceneCanvasesRef.current.delete(scene.id);
        }
    };

    const decodePerRowScene = async (row) => {
        try {
            const outer = JSON.parse(row.SceneJson);
            if (!outer || typeof outer !== 'object') return null;
            let sceneObj;
            if (outer.encoding === 'gzip+base64' && outer.blob) {
                const decompressed = await gunzipBase64ToString(outer.blob);
                sceneObj = JSON.parse(decompressed);
            } else {
                sceneObj = outer;
            }
            if (!sceneObj || typeof sceneObj !== 'object') return null;
            return { ...sceneObj, dbId: row.ID };
        } catch { return null; }
    };

    const handleOpenBgModal = async () => {
        setBgModalMode('presetgrid');
        setBgModalDraftColor(sanitiseHex(customisation?.pageBg) || '#ffffff');
        setBgModalPresetId(customisation?.presetBgId || null);
        setShowBgModal(true);
        if (tiledScenes.length > 0) return;
        setIsFetchingTiledScenes(true);
        try {
            const [perRowRes, blobRes] = await Promise.allSettled([getMyImagerScenes(), getImagerBlob()]);

            // Per-row scenes (new system)
            const perRowScenes = [];
            if (perRowRes.status === 'fulfilled' && perRowRes.value?.success) {
                const decoded = await Promise.all(
                    (perRowRes.value.scenes || []).filter(s => s.SceneJson).map(decodePerRowScene)
                );
                perRowScenes.push(...decoded.filter(Boolean));
            }

            // Legacy blob scenes (fallback for unmigrated scenes)
            const blobScenes = [];
            if (blobRes.status === 'fulfilled' && blobRes.value?.data?.blob) {
                const json = await gunzipBase64ToString(blobRes.value.data.blob);
                const payload = JSON.parse(json);
                blobScenes.push(...(Array.isArray(payload.scenes) ? payload.scenes : []));
            }

            // Merge: per-row first, then legacy-only (not yet migrated)
            const perRowIds = new Set(perRowScenes.map(s => s.id));
            const merged = [
                ...perRowScenes,
                ...blobScenes.filter(s => !perRowIds.has(s.id)),
            ].filter(s => s.tileMode);

            setTiledScenes(merged);
        } catch {
            toast.error("Failed to load tiled scenes.");
        } finally {
            setIsFetchingTiledScenes(false);
        }
    };

    const handleConfirmBgModal = async () => {
        if (bgModalMode === 'presetgrid') {
            if (bgModalPresetId === null) {
                // Colour selected
                setCustomisationValue('pageBg', bgModalDraftColor);
                setCustomisationValue('presetBgId', null);
                setProfileBgImageUrl(null);
                setPendingBgImageUrl(null);
                setShowBgModal(false);
                return;
            }
            // Preset background selected
            const preset = getPresetBackgrounds().find(p => p.id === bgModalPresetId);
            if (!preset) return;
            setCustomisationValue('presetBgId', preset.id);
            setCustomisationValue('pageBg', '');
            setProfileBgImageUrl(preset.image);
            setPendingBgImageUrl(null);
            setShowBgModal(false);
            toast.success("Background updated — save your profile to apply.");
            return;
        }
        // Scene mode
        const canvas = tiledSceneCanvasesRef.current.get(selectedBgSceneId);
        if (!canvas) {
            toast.error("Scene preview not ready yet.");
            return;
        }
        try {
            const dataUrl = await new Promise((resolve, reject) => {
                canvas.toBlob(blob => {
                    if (!blob) { resolve(canvas.toDataURL("image/png")); return; }
                    const r = new FileReader();
                    r.onload = () => resolve(r.result);
                    r.onerror = reject;
                    r.readAsDataURL(blob);
                }, "image/png");
            });
            setProfileBgImageUrl(dataUrl);
            setPendingBgImageUrl(dataUrl);
            setCustomisationValue('hasTiledBg', true);
            setCustomisationValue('presetBgId', null);
            toast.success("Background updated — save your profile to apply.");
            setShowBgModal(false);
        } catch {
            toast.error("Failed to compile background.");
        }
    };

    const handleOpenBannerModal = async () => {
        setShowBannerModal(true);
        if (imagerScenes.length > 0) return;
        setIsFetchingScenes(true);
        try {
            const [perRowRes, blobRes] = await Promise.allSettled([getMyImagerScenes(), getImagerBlob()]);

            const perRowScenes = [];
            if (perRowRes.status === 'fulfilled' && perRowRes.value?.success) {
                const decoded = await Promise.all(
                    (perRowRes.value.scenes || []).filter(s => s.SceneJson).map(decodePerRowScene)
                );
                perRowScenes.push(...decoded.filter(Boolean));
            }

            const blobScenes = [];
            if (blobRes.status === 'fulfilled' && blobRes.value?.data?.blob) {
                const json = await gunzipBase64ToString(blobRes.value.data.blob);
                const payload = JSON.parse(json);
                blobScenes.push(...(Array.isArray(payload.scenes) ? payload.scenes : []));
            }

            const perRowIds = new Set(perRowScenes.map(s => s.id));
            const merged = [
                ...perRowScenes,
                ...blobScenes.filter(s => !perRowIds.has(s.id)),
            ].filter(s => !s.tileMode);

            setImagerScenes(merged);
        } catch {
            toast.error("Failed to load scenes.");
        } finally {
            setIsFetchingScenes(false);
        }
    };

    // Auto-select first scene + eagerly generate all previews when modal loads scenes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!showBannerModal || !imagerScenes.length) return;
        const firstId = imagerScenes[0].id;
        setSelectedSceneId(prev => prev && imagerScenes.find(s => s.id === prev) ? prev : firstId);
        let cancelled = false;
        (async () => {
            for (const scene of imagerScenes) {
                if (cancelled) break;
                await generateSceneCanvas(scene);
            }
        })();
        return () => { cancelled = true; };
    }, [imagerScenes, showBannerModal]);

    // Priority: render selected scene immediately if not yet cached
    useEffect(() => {
        if (!selectedSceneId || !showBannerModal) return;
        const scene = imagerScenes.find(s => s.id === selectedSceneId);
        if (scene) generateSceneCanvas(scene);
    }, [selectedSceneId, showBannerModal]);

    // Tiled scene preview generation for bg modal
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!showBgModal || !tiledScenes.length) return;
        // Only keep the existing selection if it's still valid — never auto-select a scene
        setSelectedBgSceneId(prev => prev && tiledScenes.find(s => s.id === prev) ? prev : null);
        let cancelled = false;
        (async () => {
            for (const scene of tiledScenes) {
                if (cancelled) break;
                await generateTiledSceneCanvas(scene);
            }
        })();
        return () => { cancelled = true; };
    }, [tiledScenes, showBgModal]);

    useEffect(() => {
        if (!selectedBgSceneId || !showBgModal) return;
        const scene = tiledScenes.find(s => s.id === selectedBgSceneId);
        if (scene) generateTiledSceneCanvas(scene);
    }, [selectedBgSceneId, showBgModal]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOpenCropEditor = (scene) => {
        const src = sceneCanvasesRef.current.get(scene.id);
        if (!src) return;
        const sceneW = src.width;
        const sceneH = src.height;
        const maxX = Math.max(0, sceneW - BANNER_W);
        const maxY = Math.max(0, sceneH - BANNER_H);
        const hadSavedCrop = sceneCrops[scene.id] != null;
        const crop = getSceneCrop(scene.id);
        const x = hadSavedCrop ? clampInt(crop.x || 0, 0, maxX) : Math.round(maxX / 2);
        const y = hadSavedCrop ? clampInt(crop.y || 0, 0, maxY) : Math.round(maxY / 2);
        setSceneCrop(scene.id, { x, y });
        setCropEditorScene(scene);
    };

    const handleCropEditorChange = useCallback((sceneId, patch) => {
        const src = sceneCanvasesRef.current.get(sceneId);
        const sceneW = src?.width || 650;
        const sceneH = src?.height || 500;
        const prev = getSceneCrop(sceneId);
        const x = clampInt(Number.isFinite(patch.x) ? patch.x : (prev.x || 0), 0, Math.max(0, sceneW - BANNER_W));
        const y = clampInt(Number.isFinite(patch.y) ? patch.y : (prev.y || 0), 0, Math.max(0, sceneH - BANNER_H));
        setSceneCrop(sceneId, { x, y });
    }, [sceneCrops]);

    const handleConfirmBannerCrop = async () => {
        const scene = cropEditorScene;
        const srcCanvas = sceneCanvasesRef.current.get(scene?.id);
        if (!srcCanvas) return;
        const sceneW = srcCanvas.width;
        const sceneH = srcCanvas.height;
        const crop = getSceneCrop(scene.id);
        const sx = clampInt(crop?.x || 0, 0, Math.max(0, sceneW - BANNER_W));
        const sy = clampInt(crop?.y || 0, 0, Math.max(0, sceneH - BANNER_H));
        const out = document.createElement("canvas");
        out.width = BANNER_W;
        out.height = BANNER_H;
        const ctx = out.getContext("2d");
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(srcCanvas, sx, sy, BANNER_W, BANNER_H, 0, 0, BANNER_W, BANNER_H);
        try {
            const dataUrl = await new Promise((resolve, reject) => {
                out.toBlob(blob => {
                    if (!blob) { resolve(out.toDataURL("image/png")); return; }
                    const r = new FileReader();
                    r.onload = () => resolve(r.result);
                    r.onerror = reject;
                    r.readAsDataURL(blob);
                }, "image/png");
            });
            setBannerUrl(dataUrl);
            setPendingBannerDataUrl(dataUrl);
            toast.success("Banner preview updated — save your profile to apply.");
            setCropEditorScene(null);
            setShowBannerModal(false);
        } catch {
            toast.error("Failed to compile banner.");
        }
    };

    useEffect(() => {
        const onMove = (e) => {
            const d = cropDragRef.current;
            if (!d) return;
            const viewport = cropViewportRef.current;
            const rect = viewport?.getBoundingClientRect?.();
            if (!rect || !viewport) return;
            const ds = cropDisplayScaleRef.current || 1;
            const px = (e.clientX - rect.left) / ds;
            const py = (e.clientY - rect.top) / ds;
            handleCropEditorChange(d.sceneId, { x: px - d.offsetX, y: py - d.offsetY });
        };
        const onUp = () => { cropDragRef.current = null; };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onUp);
        };
    }, [handleCropEditorChange]);

    const loadProfileCustomisation = async (bypassCache = false) => {
        if (!profileUsername) return;
        const cacheKey = `customisation_${profileUsername.toLowerCase()}`;

        if (!bypassCache) {
            const cached = getCachedData(cacheKey);
            if (cached) {
                applyCustomisationPayload(cached);
                return cached;
            }
        }

        try {
            const r = await getProfileData(profileUsername, PROFILE_CUSTOMISATION_TYPE);
            if (!r?.success) return;
            const payload = safeParsePayload(r?.data?.jsonPayload);
            applyCustomisationPayload(payload);
            setCachedData(cacheKey, payload);
            return payload;
        } catch { toast.error("Failed to load profile customisation."); }
    };

    async function loadProfileAchievements() {
        if (!profileUsername) return;
        const cacheKey = `achievements_${profileUsername.toLowerCase()}`;

        const cached = getCachedData(cacheKey);
        if (cached && Array.isArray(cached) && (cached.length === 0 || cached[0]?.earnedCount !== undefined)) {
            setAchievements(cached);
            setAchievementsLoading(false);
            return;
        }

        try {
            setAchievementsLoading(true);
            const r = await getProfileAchievements(profileUsername);
            const achList = Array.isArray(r?.achievements) ? r.achievements : [];
            setAchievements(achList);
            setCachedData(cacheKey, achList);
        }
        catch { toast.error("Failed to load achievements."); setAchievements([]); }
        finally { setAchievementsLoading(false); }
    }

    const loadCurrentUserFriends = async () => {
        if (!isLoggedIn) { setCurrentUserFriends([]); return; }
        const selfName = String(currentUsername || "").trim();
        if (!selfName) { setCurrentUserFriends([]); return; }

        const cacheKey = `my_friends_${selfName.toLowerCase()}`;
        const cached = getCachedData(cacheKey);
        if (cached) {
            setCurrentUserFriends(cached);
            return;
        }

        try {
            const r = await listProfileFriends(selfName);
            if (r?.success && Array.isArray(r.friends)) {
                setCurrentUserFriends(r.friends);
                setCachedData(cacheKey, r.friends);
            }
            else setCurrentUserFriends([]);
        } catch {
            setCurrentUserFriends([]);
        }
    };

    const loadProfileMood = async () => {
        if (!profileUsername) return;
        try {
            const r = await getProfileData(profileUsername, 'mood');
            if (r?.success) {
                const payload = safeParsePayload(r?.data?.jsonPayload);
                setProfileMood(payload?.mood ?? '');
            }
        } catch {}
    };

    // ── Aggregate page-load effect ────────────────────────────────────────────
    useEffect(() => {
        if (!profileUsername) { setPageLayoutLoading(false); setPageInitLoading(false); return; }
        let cancelled = false;

        setPageInitData(null);
        setPageLayoutLoading(true);
        setPageInitLoading(true);
        setCustomisationLoaded(false);
        setAchievementsLoading(true);
        setFriendsLoading(true);
        setTopFriendsLoading(true);
        setRelationshipLoading(true);
        setViewerBanChecked(false);
        setViewedBanChecked(false);
        setBannerUrl(null);
        aggregateSetViewedBanRef.current = null;
        hasAnyPostRef.current = undefined;

        const cachedSkins = getCachedData(`owned_skins_${currentUserId}`);
        if (Array.isArray(cachedSkins)) setOwnedSkinIds(cachedSkins);
        else setOwnedSkinIds([]);

        (async () => {
            try {
                // ── Phase 1: layout
                const cacheKey = `customisation_${profileUsername.toLowerCase()}`;
                const cached = getCachedData(cacheKey);
                if (cached) {
                    applyCustomisationPayload(cached);
                } else {
                    try {
                        const r = await getProfileData(profileUsername, PROFILE_CUSTOMISATION_TYPE);
                        if (!cancelled && r?.success) {
                            const payload = safeParsePayload(r?.data?.jsonPayload);
                            applyCustomisationPayload(payload);
                            setCachedData(cacheKey, payload);
                        } else if (!cancelled) {
                            applyCustomisationPayload({});
                        }
                    } catch {
                        if (!cancelled) applyCustomisationPayload({});
                    }
                }
                if (!cancelled) setPageLayoutLoading(false);

                // ── Phase 2: full aggregate ───────────────────────────────────
                const data = await getProfilePageLoad(profileUsername);
                if (cancelled) return;

                if (data._httpStatus === 404) {
                    setRelationship({ success: true, exists: false });
                    setRelationshipLoading(false);
                    setViewerBanChecked(true);
                    setViewedBanChecked(true);
                    setAchievementsLoading(false);
                    setFriendsLoading(false);
                    setTopFriendsLoading(false);
                    return;
                }

                if (data._httpStatus === 403) {
                    try {
                        const banRes = await getActiveBan(currentUserId);
                        if (!cancelled) setActiveBan(banRes?.ban ?? null);
                    } catch {}
                    if (!cancelled) {
                        setViewerBanChecked(true);
                        setViewedBanChecked(true);
                        setRelationshipLoading(false);
                        setAchievementsLoading(false);
                        setFriendsLoading(false);
                        setTopFriendsLoading(false);
                    }
                    return;
                }

                // ── Customisation / layout / bio ──────────────────────────────
                applyCustomisationPayload(data.customisation ?? {});
                setCachedData(`customisation_${profileUsername.toLowerCase()}`, data.customisation ?? {});

                // ── Banner ────────────────────────────────────────────────────
                if (data.banner?.url) {
                    setBannerUrl(data.banner.url);
                    setCachedData(`banner_${profileUsername.toLowerCase()}`, data.banner);
                }

                // ── Profile background image ──────────────────────────────────
                if (data.profile_bg?.url) {
                    setProfileBgImageUrl(data.profile_bg.url);
                } else {
                    let resolvedBg = null;
                    // Fallback: load separately if not in bulk response
                    try {
                        const bgR = await getProfileData(profileUsername, "profile_bg");
                        if (!cancelled && bgR?.success) {
                            const bgP = safeParsePayload(bgR?.data?.jsonPayload);
                            if (bgP?.url) resolvedBg = bgP.url;
                        }
                    } catch {}
                    if (!cancelled) {
                        if (resolvedBg) {
                            setProfileBgImageUrl(resolvedBg);
                        } else if (data.customisation?.presetBgId) {
                            const preset = getPresetBackgrounds().find(p => p.id === data.customisation.presetBgId);
                            if (preset) setProfileBgImageUrl(preset.image);
                        }
                    }
                }

                // ── Mood ──────────────────────────────────────────────────────
                setProfileMood(data.mood?.mood ?? '');

                // ── Pinned badge ──────────────────────────────────────────────
                try {
                    const pbCacheKey = `pinnedBadge_${profileUsername.toLowerCase()}`;
                    let pbPayload = getCachedData(pbCacheKey);
                    if (pbPayload === null) {
                        const pbR = await getProfileData(profileUsername, 'pinnedBadge');
                        if (pbR?.success) {
                            pbPayload = safeParsePayload(pbR?.data?.jsonPayload);
                            setCachedData(pbCacheKey, pbPayload);
                        }
                    }
                    if (!cancelled && pbPayload !== null) {
                        setPinnedBadgeID(pbPayload?.badgeID ?? null);
                        setPinnedBadgeUploaded(pbPayload?.uploaded ?? false);
                    }
                } catch {}

                // ── Achievements ──────────────────────────────────────────────
                const achList = Array.isArray(data.achievements) ? data.achievements : [];
                setAchievements(achList);
                setAchievementsLoading(false);
                setCachedData(`achievements_${profileUsername.toLowerCase()}`, achList);

                // ── Friends + Top Friends ─────────────────────────────────────
                const fData = data.friends || {};
                setFriends((fData.friends || []).map(f => ({ ...f, profileBg: f.profileBg || '#5A2929' })));
                setFriendsTotal(fData.total || 0);
                setFriendsHasMore(Boolean(fData.hasMore));
                setFriendsOffset(0);
                setFriendsLoading(false);
                setTopFriends((data.topFriends || []).map(f => ({ ...f, profileBg: f.profileBg || '#5A2929' })));
                setTopFriendsLoading(false);

                // ── Luv summary
                if (data.luvSummary) {
                    setCachedData(`luv_summary_${profileUsername.toLowerCase()}`, data.luvSummary.total);
                }

                // ── Relationship ──────────────────────────────────────────────
                setRelationship(data.relationship ?? null);
                setRelationshipLoading(false);
                aggregateSetRelationshipRef.current = { username: profileUsername, isLoggedIn };

                // ── Viewer restrictions & ban ─────────────────────────────────
                setActiveRestrictions(data.viewerRestrictions ?? null);
                setActiveBan(null);
                setViewerBanChecked(true);

                if (Array.isArray(data.posts?.posts)) {
                    hasAnyPostRef.current = data.posts.posts.length > 0;
                }

                // ── Viewed user ban ───────────────────────────────────────────
                setViewedUserBan(null);
                setViewedBanChecked(true);
                aggregateSetViewedBanRef.current = { username: profileUsername };

                setPageInitData(data);
                if (Array.isArray(data.ownedSkins)) {
                    setOwnedSkinIds(data.ownedSkins);
                    setCachedData(`owned_skins_${currentUserId}`, data.ownedSkins);
                }
            } catch (err) {
                if (cancelled) return;
                setRelationshipLoading(false);
                setViewerBanChecked(true);
                setViewedBanChecked(true);
                setAchievementsLoading(false);
                setFriendsLoading(false);
                setTopFriendsLoading(false);
            } finally {
                if (!cancelled) {
                    setPageLayoutLoading(false);
                    setPageInitLoading(false);
                }
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileUsername]);

    const fetchRelationship = async () => {
        if (!isLoggedIn || (!isSteve && isOwnProfile) || !profileUsername) { setRelationshipLoading(false); setRelationship(null); return; }
        const agr = aggregateSetRelationshipRef.current;
        if (agr?.username === profileUsername && agr?.isLoggedIn === isLoggedIn) return;
        try {
            setRelationshipLoading(true);
            const data = await getProfileRelationship(profileUsername);
            setRelationship(data?.success ? data : null);
        } catch { toast.error("Failed to load relationship status."); setRelationship(null); }
        finally { setRelationshipLoading(false); }
    };

    useEffect(() => { setIsEditing(false); setCustomisationLoaded(false); }, [profileUsername]);
    useEffect(() => { fetchRelationship(); }, [profileUsername, isLoggedIn, isOwnProfile, isSteve]);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            setViewerBanChecked(false);

            if (!isLoggedIn) {
                setActiveRestrictions(null);
                setActiveBan(null);
                setViewerBanChecked(true);
                return;
            }

            const currentUserId = String(
                userData?.id ?? userData?.ID ?? userData?.userId ?? userData?.userID ?? userData?.UserID ?? userData?.user_id ?? ''
            );

            if (!currentUserId) {
                setActiveRestrictions(null);
                setActiveBan(null);
                setViewerBanChecked(true);
                return;
            }

            try {
                const [restrictRes, banRes] = await Promise.all([
                    getActiveRestrictions(currentUserId),
                    getActiveBan(currentUserId),
                ]);

                if (cancelled) return;

                setActiveRestrictions(restrictRes?.restriction ?? null);
                setActiveBan(banRes?.ban ?? null);
            } catch {
                if (cancelled) return;
                setActiveRestrictions(null);
                setActiveBan(null);
            } finally {
                if (!cancelled) setViewerBanChecked(true);
            }
        };

        run();
        return () => { cancelled = true; };
    }, [isLoggedIn, userData]);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            setViewedBanChecked(false);

            if (isOwnProfile) {
                setViewedUserBan(null);
                setViewedBanChecked(true);
                return;
            }

            if (aggregateSetViewedBanRef.current?.username === profileUsername) {
                setViewedUserBan(null);
                setViewedBanChecked(true);
                return;
            }

            if (!relationship) {
                return;
            }

            const rel = relationship || {};
            const id = rel.profileUserID ?? rel.profileUserId ?? rel.userID ?? rel.userId ?? rel.targetUserID ?? rel.targetUserId ?? null;
            const viewedId = String(id ?? '').trim();

            if (!viewedId) {
                setViewedUserBan(null);
                setViewedBanChecked(true);
                return;
            }

            try {
                const r = await getActiveBan(viewedId);
                if (cancelled) return;
                setViewedUserBan(r?.ban ?? null);
            } catch {
                if (cancelled) return;
                setViewedUserBan(null);
            } finally {
                if (!cancelled) setViewedBanChecked(true);
            }
        };

        run();
        return () => { cancelled = true; };
    }, [isOwnProfile, relationship]);

    const loadProfileFriends = async (mode = "reset") => {
        if (!profileUsername) return;

        const nextOffset = mode === "append" ? friendsOffset + FRIENDS_PAGE_SIZE : 0;

        try {
            if (mode === "append") setFriendsLoadingMore(true);
            else setFriendsLoading(true);

            const r = await listProfileFriends(profileUsername, FRIENDS_PAGE_SIZE, nextOffset);

            const incoming = (r?.success && Array.isArray(r.friends))
                ? r.friends.map(f => ({ ...f, profileBg: f.profileBg || "#5A2929" }))
                : [];

            const total = Number(r?.total || 0);
            const hasMore = Boolean(r?.hasMore);

            setFriendsTotal(total);
            setFriendsHasMore(hasMore);
            setFriendsOffset(nextOffset);

            if (mode === "append") {
                setFriends(prev => {
                    const existing = new Set((prev || []).map(x => String(x?.userID || "")));
                    const merged = Array.isArray(prev) ? [...prev] : [];
                    for (const f of incoming) {
                        const id = String(f?.userID || "");
                        if (id && !existing.has(id)) merged.push(f);
                    }
                    return merged;
                });
            } else {
                setFriends(incoming);
            }
        } catch {
            toast.error("Failed to load friends.");
            if (mode !== "append") setFriends([]);
        } finally {
            if (mode === "append") setFriendsLoadingMore(false);
            else setFriendsLoading(false);
        }
    };

    useEffect(() => { loadCurrentUserFriends(); }, [isLoggedIn, currentUsername]);

    useEffect(() => { setFriendsTab('top'); setSuggestionsLoaded(false); setSuggestions([]); setShowAllFriends(false); }, [profileUsername]);

    if (pageLayoutLoading) {
        return <div className="w-full min-h-screen p-4 lg2:p-6 overflow-hidden flex items-center justify-center"><div className="text-gold font-['Jersey_10'] text-3xl text-center">Loading profile...</div></div>;
    }

    if (!profileUsername) {
        return <div className="w-full min-h-screen p-4 lg2:p-6 overflow-hidden"><ContentBox theme="default" header="Profile"><div className="text-coal">No username supplied.</div></ContentBox></div>;
    }

    const shouldShowNotFound = !pageLayoutLoading && !pageInitLoading && relationship && (relationship.exists === false || relationship.isBlockedBy);
    if (shouldShowNotFound) {
        return <div className="w-full min-h-screen p-4 lg2:p-6 overflow-hidden flex items-center justify-center"><div className="text-gold font-['Jersey_10'] text-3xl text-center">This profile doesn't exist!</div></div>;
    }

    const banGateReady =
        pageInitLoading ||
        ((!isLoggedIn || viewerBanChecked) && (isOwnProfile || viewedBanChecked));

    if (!banGateReady) {
        return <div className="w-full min-h-screen p-4 lg2:p-6 overflow-hidden" />;
    }
    if (!pageInitLoading && activeBan) {
        const banExpiry = activeBan.expiresAt ? new Date(activeBan.expiresAt).toLocaleString() : null;
        return (
            <div className="w-full min-h-screen p-4 lg2:p-6 overflow-hidden flex items-center justify-center">
                <div className="max-w-md w-full border border-black/30 bg-offwhite rounded shadow-md overflow-hidden">
                    <div className="px-4 py-3 border-b border-black/20 bg-red-600 text-white font-bold text-lg font-['Jersey_10']">
                        Profile Banned
                    </div>
                    <div className="p-4 text-coal text-sm flex flex-col gap-3">
                        <p>You have been banned from using Frankly Profiles.</p>
                        {activeBan.reason && (
                            <p><span className="font-bold">Reason:</span> {activeBan.reason}</p>
                        )}
                        {banExpiry ? (
                            <p><span className="font-bold">Expires:</span> {banExpiry}</p>
                        ) : (
                            <p className="font-bold text-red-700">This ban is permanent.</p>
                        )}
                        <p className="text-coal/60 text-xs mt-1">If you believe this is an error, please contact staff.</p>
                    </div>
                </div>
            </div>
        );
    }
    if (!pageInitLoading && viewedUserBan && !isOwnProfile) {
        return (
            <div className="w-full min-h-screen p-4 lg2:p-6 overflow-hidden flex items-center justify-center">
                <div className="text-gold font-['Jersey_10'] text-3xl text-center">
                    This profile doesn't exist!
                </div>
            </div>
        );
    }

    const isBlockingTarget = !!relationship?.isBlocking;
    const showOnlyBio = !isOwnProfile && isBlockingTarget;
    const canShowActions = isLoggedIn && !guestMode;
    const actionsDisabled = isOwnProfile;
    const isPendingOutgoing = !!relationship?.isPendingOutgoing;
    const friendButtonLabel = relationship?.isFriend ? "Unfriend" : (isPendingOutgoing ? "Pending" : "Add friend");
    const blockButtonLabel = relationship?.isBlocking ? "Unblock" : "Block";

    const onFriendClick = async () => {
        if (!canShowActions || actionsDisabled || friendActionLoading || isPendingOutgoing) return;
        setFriendActionLoading(true);
        try {
            if (relationship?.isFriend) {
                const r = await removeProfileFriend(profileUsername);
                if (r?.success !== true) toast.error("Failed to remove friend.");
                const friendToRemove = friends.find(f => f.habboName.toLowerCase() === profileUsername.toLowerCase());
                if (friendToRemove && isOwnProfile) {
                    setCustomisation((prev) => {
                        const currentOrder = prev?.topFriendsOrder || [];
                        const newTopOrder = currentOrder.filter(id => id !== friendToRemove.userID);
                        if (newTopOrder.length === currentOrder.length) return prev;
                        return { ...(prev || {}), topFriendsOrder: newTopOrder };
                    });
                    setTopFriends((prev) => prev.filter(f => f.userID !== friendToRemove.userID));
                }
                if (r?.relationship) setRelationship({ success: true, ...r.relationship });
                else await fetchRelationship();
                await loadProfileFriends();
            } else {
                const r = await sendProfileFriendRequest(profileUsername);
                if (r?.success !== true) { toast.error("Failed to send friend request."); await fetchRelationship(); }
                else setRelationship((prev) => prev?.success ? { ...prev, isPendingOutgoing: true, isFriend: false, isPendingIncoming: false } : prev);
            }
        } catch { toast.error("Friend action failed. Please try again."); }
        finally { setFriendActionLoading(false); }
    };

    const onBlockClick = () =>
        requestConfirm({
            title: relationship?.isBlocking ? "Unblock user?" : "Block user?",
            message: relationship?.isBlocking ? unblockConfirmBody(profileUsername) : blockConfirmBody(profileUsername),
            onConfirm: runToggle
        });

    const handleEditToggle = async () => {
        if (!isOwnProfile) return;

        if (!isEditing && activeRestrictions?.restrictCustomisation) {
            const expiry = activeRestrictions.expiresAt ? ` until ${new Date(activeRestrictions.expiresAt).toLocaleString()}` : '';
            toast.warning(`You are restricted from customising your profile${expiry}.`);
            return;
        }

        if (!isEditing) {
            const freshPayload = await loadProfileCustomisation(true);
            setOriginalCustomisation(freshPayload ?? buildCurrentCustomisationPayload());
            setOriginalBannerUrl(bannerUrl);
            setOriginalBgImageUrl(profileBgImageUrl);
            setPendingBgImageUrl(undefined);
            setIsEditing(true);
            return;
        }

        const nextPayload = buildCurrentCustomisationPayload();
        const changed = stableStringify(nextPayload) !== stableStringify(originalCustomisation ?? {});

        if (changed) {
            try {
                const activeSkins = Object.values(nextPayload?.boxSkins ?? {});
                const hasUnownedSkin = activeSkins.some(
                    skinId => !isSkinSaveable(skinId, isStaff, ownedSkinIds)
                );
                if (hasUnownedSkin) {
                    toast.error("You're using skins you don't own!");
                    return;
                }
                if (nextPayload?.luvHeartSkin && !isSkinSaveable(nextPayload.luvHeartSkin, isStaff, ownedSkinIds)) {
                    toast.error("You don't own this Luv heart skin!");
                    return;
                }
                if (nextPayload?.presetBgId) {
                    const preset = getPresetBackgrounds().find(p => p.id === nextPayload.presetBgId);
                    if (preset && !isSkinSaveable(preset.skinId, isStaff, ownedSkinIds)) {
                        toast.error("You don't own the skin required for this background.");
                        return;
                    }
                }
                const r = await upsertOwnProfileData(PROFILE_CUSTOMISATION_TYPE, nextPayload);

                if (!r?.success) {
                    const errorDetail = r?.message || r?.error || "Unknown server error";
                    console.error("Profile Save Rejected:", { errorDetail, payload: nextPayload });
                    toast.error(`Failed to save: ${errorDetail}`);
                } else {
                    toast.success("Profile saved!");
                    clearCachedData(`customisation_${profileUsername.toLowerCase()}`);
                    invalidateProfileColor(profileUsername);
                    await loadTopFriends();
                    await runPostSaveProfileBadgeChecks();
                }
                setCustomisation(nextPayload);
            } catch (err) {
                console.error("Profile Save Request Failed:", err);
                toast.error(`System Error: ${err.message || "Could not connect to server"}`);
            }
        }

        if (pendingBannerDataUrl) {
            try {
                const bannerPayload = { url: pendingBannerDataUrl };
                const br = await upsertOwnProfileData("banner", bannerPayload);
                if (br?.success) {
                    setCachedData(`banner_${profileUsername.toLowerCase()}`, bannerPayload);
                } else {
                    toast.error("Failed to save banner.");
                }
            } catch {
                toast.error("Failed to save banner.");
            }
            setPendingBannerDataUrl(null);
        }

        if (pendingBgImageUrl !== undefined) {
            try {
                const bgPayload = pendingBgImageUrl ? { url: pendingBgImageUrl } : { url: null };
                await upsertOwnProfileData("profile_bg", bgPayload);
            } catch {
                toast.error("Failed to save profile background.");
            }
            setPendingBgImageUrl(undefined);
        }

        setOriginalBannerUrl(null);
        setOriginalBgImageUrl(null);
        setOriginalCustomisation(null);
        setIsEditing(false);
        setEditingHeader(null);
        setPreviewAsGuest(false);
    };
    const renderBoxByKey = (boxKey, columnId = 'right') => {
        const box = BOX_REGISTRY.find(b => b.key === boxKey);
        if (!box) return null;
        if (pageInitLoading) {
            return (
                <EditableBox
                    boxKey={boxKey}
                    header={box.label}
                    headerTitle={getHeaderTitle(boxKey)}
                    isEditing={false}
                    isOwnProfile={isOwnProfile}
                    getHeaderColour={getHeaderColour}
                    setHeaderColour={setHeaderColour}
                    getBoxSkin={getBoxSkin}
                    setBoxSkin={setBoxSkin}
                    editingHeader={null}
                    setEditingHeader={() => {}}
                    setHeaderTitle={() => {}}
                    isStaff={isStaff}
                    ownedSkinIds={ownedSkinIds}
                >
                    <div className="text-coal/50 text-sm p-3">Loading...</div>
                </EditableBox>
            );
        }
        return box.render(columnId);
    };

    // ─── Guest visibility gate ───────────────────────────────────────────────
    if (guestMode && !customisationLoaded) {
        return <div className="w-full min-h-screen p-8 flex items-center justify-center"><div className="text-gold font-['Jersey_10'] text-xl">Loading...</div></div>;
    }
    if (guestMode && !profileIsPublic) {
        return (
            <div className="w-full min-h-screen p-8 flex items-center justify-center">
                <div className="bg-offwhite border border-gray-300 shadow-[0_3px_0_0_rgba(0,0,0,0.5)] px-8 py-6 text-center font-['Jersey_10'] text-2xl text-coal tracking-wide">
                    This profile is private. Please login to view it.
                </div>
            </div>
        );
    }
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <>
            {isEditing && (
                <div
                    className="absolute top-[174px] left-0 right-0 z-[1] h-[120px] flex items-center justify-center gap-3 px-4 py-2 font-['Jersey_10'] text-2xl tracking-wide select-none"
                    style={{ background: 'rgba(20,10,10,0.82)', backdropFilter: 'blur(4px)', borderBottom: '2px solid rgba(255,180,0,0.4)', borderTop: '2px solid rgba(255,180,0,0.4)', color: '#ffd966', textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    YOU ARE IN EDIT MODE - SAVE OR CANCEL BEFORE NAVIGATING
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                </div>
            )}
            <>
                <DndContext
                    sensors={sensors}
                    collisionDetection={(args) => { const pointerHits = pointerWithin(args); if (pointerHits?.length) return pointerHits; return rectIntersection(args); }}
                    measuring={{ droppable: { strategy: MeasuringStrategy.BeforeDragging } }}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                >
                    <div className="w-full min-h-screen p-4 lg2:p-6 overflow-hidden transition-colors duration-200" style={{ backgroundColor: sanitiseHex(customisation?.pageBg) || undefined, backgroundImage: profileBgImageUrl ? `url(${profileBgImageUrl})` : undefined, backgroundRepeat: profileBgImageUrl ? 'repeat' : undefined, backgroundSize: profileBgImageUrl ? 'auto' : undefined, paddingBottom: isOwnProfile && !guestMode ? '80px' : undefined }}>
                        <div className="grid grid-cols-1 gap-4 lg2:gap-6 lg2:grid-cols-[360px_minmax(0,1fr)]">
                            <div className="min-w-0 flex flex-col gap-4">
                                <BioBox
                                    profileUsername={profileUsername}
                                    bannerUrl={bannerUrl}
                                    effectiveIsEditing={effectiveIsEditing}
                                    effectiveIsOwnProfile={effectiveIsOwnProfile}
                                    handleOpenBannerModal={handleOpenBannerModal}
                                    openCircleBgModal={openCircleBgModal}
                                    customisation={customisation}
                                    sanitiseHex={sanitiseHex}
                                    canShowActions={canShowActions}
                                    isBlockingTarget={isBlockingTarget}
                                    actionsDisabled={actionsDisabled}
                                    relationshipLoading={relationshipLoading}
                                    friendActionLoading={friendActionLoading}
                                    isPendingOutgoing={isPendingOutgoing}
                                    onFriendClick={onFriendClick}
                                    friendButtonLabel={friendButtonLabel}
                                    onBlockClick={onBlockClick}
                                    blockActionLoading={blockActionLoading}
                                    blockButtonLabel={blockButtonLabel}
                                    editingPronouns={editingPronouns}
                                    setEditingPronouns={setEditingPronouns}
                                    pronounPart1={pronounPart1} pronounPart2={pronounPart2}
                                    setPronounPart1={setPronounPart1} setPronounPart2={setPronounPart2}
                                    pronounCustom1={pronounCustom1} pronounCustom2={pronounCustom2}
                                    setPronounCustom1={setPronounCustom1} setPronounCustom2={setPronounCustom2}
                                    setCustomisationValue={setCustomisationValue}
                                    showOnlyBio={showOnlyBio}
                                    bio={bio} setBio={setBio}
                                    bioHtmlObj={bioHtmlObj}
                                    emoteCtx={emoteCtx}
                                    openEmotePicker={openEmotePicker}
                                    profileMood={profileMood}
                                    saveMood={saveMood}
                                    isEditing={isEditing}
                                    guestMode={guestMode}
                                    isLoggedIn={isLoggedIn}
                                    openReportModal={openReportModal}
                                    getHeaderColour={getHeaderColour} setHeaderColour={setHeaderColour}
                                    getBoxSkin={getBoxSkin} setBoxSkin={setBoxSkin}
                                    editingHeader={editingHeader} setEditingHeader={setEditingHeader}
                                    getHeaderTitle={getHeaderTitle} setHeaderTitle={setHeaderTitle}
                                    hasSTFSkin={isStaff} ownedSkinIds={ownedSkinIds}
                                    pinnedBadgeID={pinnedBadgeID ?? (achievements[0]?.badgeID ?? null)}
                                    pinnedBadgeUploaded={pinnedBadgeID ? pinnedBadgeUploaded : (achievements[0]?.uploaded ?? false)}
                                    pinnedBadgeAchievement={(() => { const id = pinnedBadgeID ?? achievements[0]?.badgeID ?? null; return id ? achievements.find(a => a.badgeID === id) ?? null : null; })()}
                                    timeAgo={timeAgo}
                                />

                                {!showOnlyBio && (
                                    <ColumnDroppable id="left-column">
                                        <SortableContext items={visibleLayout.left} strategy={verticalListSortingStrategy}>
                                            <div className="min-w-0 flex flex-col gap-4">
                                                {visibleLayout.left.map((k) => (
                                                    <SortableBox key={k} id={k} columnId="left" isEditing={effectiveIsEditing} onHeaderProps={{ isEditing: effectiveIsEditing, isOwnProfile: effectiveIsOwnProfile, getHeaderColour, setHeaderColour, getBoxSkin, setBoxSkin, editingHeader, setEditingHeader, getHeaderTitle, setHeaderTitle }}>
                                                        {renderBoxByKey(k, 'left')}
                                                    </SortableBox>
                                                ))}
                                                {visibleLayout.left.length === 0 && effectiveIsEditing && (
                                                    <div className="border-2 border-dashed border-black/30 bg-offwhite/50 h-[120px] flex items-center justify-center text-coal/70">Drop here</div>
                                                )}
                                            </div>
                                        </SortableContext>
                                    </ColumnDroppable>
                                )}
                            </div>

                            {!showOnlyBio && (
                                <ColumnDroppable id="right-column">
                                    <SortableContext items={visibleLayout.right} strategy={verticalListSortingStrategy}>
                                        <div className="min-w-0 flex flex-col gap-4">
                                            {visibleLayout.right.map((k) => (
                                                <SortableBox key={k} id={k} columnId="right" isEditing={effectiveIsEditing} onHeaderProps={{ isEditing: effectiveIsEditing, isOwnProfile: effectiveIsOwnProfile, getHeaderColour, setHeaderColour, getBoxSkin, setBoxSkin, editingHeader, setEditingHeader, getHeaderTitle, setHeaderTitle }}>
                                                    {renderBoxByKey(k, 'right')}
                                                </SortableBox>
                                            ))}
                                            {visibleLayout.right.length === 0 && effectiveIsEditing && (
                                                <div className="border-2 border-dashed border-black/30 bg-offwhite/50 h-[120px] flex items-center justify-center text-coal/70">Drop here</div>
                                            )}
                                        </div>
                                    </SortableContext>
                                </ColumnDroppable>
                            )}
                        </div>
                    </div>

                    <DragOverlay>{activeId ? <div style={{ opacity: 0.8 }}>{renderBoxByKey(activeId)}</div> : null}</DragOverlay>
                </DndContext>

                {confirmState && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-3">
                        <div className="w-full max-w-sm border border-black/40 bg-offwhite rounded shadow-md overflow-hidden">
                            <div className="px-3 py-2 border-b border-black/20 font-bold text-coal">{confirmState.title}</div>
                            <div className="p-3 text-sm text-coal">{confirmState.message}</div>
                            <div className="p-2 border-t border-black/20 flex justify-end gap-2 bg-black/5">
                                <button type="button" onClick={closeConfirm} className="px-3 py-1 border border-black/40 bg-offwhite text-coal text-sm rounded hover:bg-black/5">No</button>
                                <button type="button" onClick={runConfirm} className="px-3 py-1 border border-black/40 bg-gold text-coal text-sm rounded hover:brightness-95">Yes</button>
                            </div>
                        </div>
                    </div>
                )}

                {reportModalOpen && (
                    <ReportProfileModal
                        profileUsername={profileUsername}
                        onClose={() => setReportModalOpen(false)}
                    />
                )}

                {showBannerModal && (() => {
                    const selectedScene = imagerScenes.find(s => s.id === selectedSceneId) || null;
                    const cropSrc = sceneCanvasesRef.current.get(cropEditorScene?.id);
                    const csW = cropSrc?.width || 650;
                    const csH = cropSrc?.height || 500;
                    const cropMaxH = Math.round(window.innerHeight * 0.62);
                    const cropMaxW = Math.min(csW, Math.round(window.innerWidth * 0.88) - 32);
                    const displayScale = cropEditorScene
                        ? Math.min(1, cropMaxW / csW, cropMaxH / csH)
                        : 1;
                    cropDisplayScaleRef.current = displayScale;
                    const displayW = Math.round(csW * displayScale);
                    const displayH = Math.round(csH * displayScale);

                    return (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                            <div className="bg-offwhite border-2 border-black shadow-xl flex flex-col max-h-[92vh]"
                                 style={{ width: cropEditorScene ? "auto" : "min(860px, 96vw)" }}>

                                {/* Header */}
                                <div className="px-4 py-3 bg-coal text-offwhite font-['Jersey_10'] text-2xl uppercase flex justify-between items-center shrink-0">
                                    {cropEditorScene ? (
                                        <div className="flex items-center gap-3">
                                            <button onClick={() => setCropEditorScene(null)}
                                                className="hover:text-gold text-lg leading-none">← Back</button>
                                            <span>Adjust Crop</span>
                                        </div>
                                    ) : (
                                        <span>Select a Saved Image</span>
                                    )}
                                    <button
                                        onClick={() => { setCropEditorScene(null); setShowBannerModal(false); }}
                                        className="hover:text-gold text-3xl leading-none"
                                    >&times;</button>
                                </div>

                                {cropEditorScene ? (
                                    /* ── Crop screen ─────────────────────────────────────── */
                                    <div className="flex flex-col gap-3 p-4 overflow-y-auto habbo-scroll">
                                        <div
                                            ref={cropViewportRef}
                                            className="relative bg-coal_dark border border-black/20 overflow-hidden shrink-0"
                                            style={{ width: displayW, height: displayH }}
                                        >
                                            <canvas
                                                ref={cropCanvasRef}
                                                className="block"
                                                style={{ width: displayW, height: displayH, imageRendering: "pixelated" }}
                                            />
                                            {(() => {
                                                const s = cropEditorScene;
                                                const crop = getSceneCrop(s.id);
                                                const maxX = Math.max(0, csW - BANNER_W);
                                                const maxY = Math.max(0, csH - BANNER_H);
                                                const boxL = clampInt(crop.x || 0, 0, maxX) * displayScale;
                                                const boxT = clampInt(crop.y || 0, 0, maxY) * displayScale;
                                                const boxW = BANNER_W * displayScale;
                                                const boxH = BANNER_H * displayScale;
                                                return (
                                                    <div
                                                        onPointerDown={(e) => {
                                                            const ds = cropDisplayScaleRef.current || 1;
                                                            const rect = cropViewportRef.current?.getBoundingClientRect?.();
                                                            if (!rect) return;
                                                            const px = (e.clientX - rect.left) / ds;
                                                            const py = (e.clientY - rect.top)  / ds;
                                                            cropDragRef.current = {
                                                                sceneId: s.id,
                                                                offsetX: px - clampInt(crop.x || 0, 0, maxX),
                                                                offsetY: py - clampInt(crop.y || 0, 0, maxY),
                                                            };
                                                            try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
                                                        }}
                                                        className="absolute border-2 border-gold cursor-move"
                                                        style={{
                                                            left: boxL, top: boxT,
                                                            width: boxW, height: boxH,
                                                            boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
                                                        }}
                                                    />
                                                );
                                            })()}
                                        </div>

                                        <div className="flex flex-wrap gap-x-6 gap-y-3 items-center">
                                            <div className="flex items-center gap-2 text-sm font-['Jersey_10']">
                                                <span className="uppercase">Crop</span>
                                                <span>{BANNER_W}×{BANNER_H}</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const maxX = Math.max(0, csW - BANNER_W);
                                                    const maxY = Math.max(0, csH - BANNER_H);
                                                    handleCropEditorChange(cropEditorScene.id, {
                                                        x: Math.round(maxX / 2),
                                                        y: Math.round(maxY / 2),
                                                    });
                                                }}
                                                className="px-3 py-1.5 border border-black bg-white text-black font-['Jersey_10'] uppercase text-sm"
                                                style={{ boxShadow: "0 3px 0 0 rgba(0,0,0,0.3)" }}
                                            >Centre</button>
                                        </div>

                                        <div className="flex justify-end">
                                            <button
                                                type="button"
                                                onClick={handleConfirmBannerCrop}
                                                className="px-6 py-2 bg-gold text-coal font-['Jersey_10'] text-xl uppercase border-2 border-black"
                                                style={{ boxShadow: "0 4px 0 0 rgba(0,0,0,0.4)" }}
                                            >Save as Banner</button>
                                        </div>
                                    </div>
                                ) : (
                                    /* ── Scene list + preview ──────────────────────────── */
                                    <div className="flex overflow-hidden" style={{ height: "70vh" }}>
                                        {/* Left: scene name list */}
                                        <div className="w-52 shrink-0 flex flex-col bg-black/5 border-r border-black/10">
                                            <div className="px-3 pt-3 pb-1 text-xs font-['Jersey_10'] uppercase text-coal/50 tracking-widest">
                                                Saved Images
                                            </div>
                                            <div className="flex-1 overflow-y-auto habbo-scroll-v p-2 flex flex-col gap-1">
                                                {isFetchingScenes ? (
                                                    <div className="text-center py-6 font-['Jersey_10'] text-coal/50 animate-pulse">Loading…</div>
                                                ) : imagerScenes.length === 0 ? (
                                                    <div className="flex flex-col items-center gap-4 py-8 px-2">
                                                        <div className="font-['Jersey_10'] text-sm text-coal/50 text-center uppercase">
                                                            No saved images. Force Save a scene in the ProImager first.
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => { setShowBannerModal(false); navigate("/goodies/imager"); }}
                                                            className="px-4 py-1.5 bg-gold text-coal font-['Jersey_10'] text-sm uppercase border border-black"
                                                            style={{ boxShadow: "0 3px 0 0 rgba(0,0,0,0.4)" }}
                                                        >Open ProImager</button>
                                                    </div>
                                                ) : imagerScenes.map(scene => (
                                                    <button
                                                        key={scene.id}
                                                        onClick={() => setSelectedSceneId(scene.id)}
                                                        title={scene.imageName || "(Untitled)"}
                                                        className={[
                                                            "px-3 py-2 text-left font-['Jersey_10'] text-sm uppercase truncate border transition-none",
                                                            selectedSceneId === scene.id
                                                                ? "bg-gold border-black text-coal"
                                                                : "bg-offwhite border-gray-300 hover:bg-gray-100 text-coal",
                                                        ].join(" ")}
                                                    >
                                                        {scene.imageName || "(Untitled)"}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Right: preview panel */}
                                        <div className="flex-1 flex flex-col min-w-0">
                                            <div className="flex-1 bg-coal_dark flex items-center justify-center overflow-hidden">
                                                {selectedSceneId && scenePreviews.get(selectedSceneId) ? (
                                                    <img
                                                        src={scenePreviews.get(selectedSceneId)}
                                                        alt="Scene preview"
                                                        className="max-w-full max-h-full block"
                                                        style={{ imageRendering: "pixelated" }}
                                                    />
                                                ) : selectedSceneId ? (
                                                    <div className="font-['Jersey_10'] text-xl text-white/40 animate-pulse">Generating preview…</div>
                                                ) : (
                                                    <div className="font-['Jersey_10'] text-xl text-white/25">Select an image</div>
                                                )}
                                            </div>
                                            <div className="px-4 py-3 bg-offwhite border-t border-black/10 flex items-center justify-between gap-4">
                                                <div className="font-['Jersey_10'] text-sm text-coal/50 uppercase truncate">
                                                    {selectedScene?.imageName || (selectedSceneId ? "(Untitled)" : "No image selected")}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => selectedScene && scenePreviews.get(selectedSceneId) && handleOpenCropEditor(selectedScene)}
                                                    disabled={!selectedScene || !scenePreviews.get(selectedSceneId)}
                                                    className="px-6 py-1.5 bg-gold text-coal font-['Jersey_10'] text-xl uppercase border border-black disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                                                    style={{ boxShadow: "0 3px 0 0 rgba(0,0,0,0.4)" }}
                                                >Crop →</button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* ── Profile Background Modal ─────────────────────────────── */}
                {showBgModal && (() => {
                    const selectedTiledScene = tiledScenes.find(s => s.id === selectedBgSceneId) || null;
                    return (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                            <div className="bg-offwhite border-2 border-black shadow-xl flex flex-col max-h-[92vh]"
                                 style={{ width: "min(860px, 96vw)" }}>
                                {/* Header */}
                                <div className="px-4 py-3 bg-coal text-offwhite font-['Jersey_10'] text-2xl uppercase flex justify-between items-center shrink-0">
                                    <span>Select Background</span>
                                    <button onClick={() => setShowBgModal(false)} className="hover:text-gold text-3xl leading-none">&times;</button>
                                </div>

                                <div className="flex overflow-hidden" style={{ height: "70vh" }}>
                                    {/* Left: option list */}
                                    <div className="w-52 shrink-0 flex flex-col bg-black/5 border-r border-black/10">
                                        <div className="px-3 pt-3 pb-1 text-xs font-['Jersey_10'] uppercase text-coal/50 tracking-widest">Options</div>
                                        <div className="flex-1 overflow-y-auto habbo-scroll-v p-2 flex flex-col gap-1">
                                            {/* Preset Backgrounds option */}
                                            <button
                                                onClick={() => setBgModalMode('presetgrid')}
                                                className={["px-3 py-2 text-left font-['Jersey_10'] text-sm uppercase truncate border transition-none", bgModalMode === 'presetgrid' ? "bg-gold border-black text-coal" : "bg-offwhite border-gray-300 hover:bg-gray-100 text-coal"].join(" ")}
                                            >
                                                Preset Backgrounds
                                            </button>
                                            {/* Tiled scenes */}
                                            {isFetchingTiledScenes ? (
                                                <div className="text-center py-4 font-['Jersey_10'] text-coal/50 animate-pulse text-sm">Loading…</div>
                                            ) : tiledScenes.length === 0 ? (
                                                <div className="font-['Jersey_10'] text-xs text-coal/40 text-center py-4 uppercase px-2">No tiled scenes. Create a tiled scene in the ProImager first.</div>
                                            ) : tiledScenes.map(scene => (
                                                <button
                                                    key={scene.id}
                                                    onClick={() => { setBgModalMode('scene'); setSelectedBgSceneId(scene.id); }}
                                                    title={scene.imageName || "(Untitled)"}
                                                    className={["px-3 py-2 text-left font-['Jersey_10'] text-sm uppercase truncate border transition-none", bgModalMode === 'scene' && selectedBgSceneId === scene.id ? "bg-gold border-black text-coal" : "bg-offwhite border-gray-300 hover:bg-gray-100 text-coal"].join(" ")}
                                                >
                                                    {scene.imageName || "(Untitled)"}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Right: preview / color picker */}
                                    <div className="flex-1 flex flex-col min-w-0">
                                        {bgModalMode === 'presetgrid' ? (
                                            <div className="flex-1 overflow-y-auto habbo-scroll-v p-3 flex flex-col gap-3">
                                                <div className="grid grid-cols-3 gap-3">
                                                    {/* Colour cell */}
                                                    <div
                                                        className={["flex flex-col items-center gap-1.5 p-2 border-2 transition-none cursor-pointer", bgModalPresetId === null ? "border-black bg-gold/10" : "border-gray-300 hover:border-gray-400 bg-offwhite"].join(" ")}
                                                        onClick={() => setBgModalPresetId(null)}
                                                    >
                                                        <div className="relative w-full aspect-video border border-gray-300 overflow-hidden shrink-0">
                                                            <ColorPicker
                                                                value={bgModalDraftColor}
                                                                onChange={(e) => { setBgModalDraftColor(e.target.value); setBgModalPresetId(null); }}
                                                                className="absolute inset-0 w-full h-full cursor-pointer border-0"
                                                                style={{ backgroundColor: bgModalDraftColor }}
                                                            />
                                                        </div>
                                                        <span className="font-['Jersey_10'] text-xs uppercase text-coal leading-none">Custom Colour</span>
                                                    </div>
                                                    {/* Preset cells */}
                                                    {getPresetBackgrounds().map(preset => {
                                                        const saveable = isSkinSaveable(preset.skinId, isStaff, ownedSkinIds);
                                                        const isSelected = bgModalPresetId === preset.id;
                                                        return (
                                                            <button
                                                                key={preset.id}
                                                                type="button"
                                                                onClick={() => setBgModalPresetId(preset.id)}
                                                                className={["flex flex-col items-center gap-1.5 p-2 border-2 transition-none", isSelected ? "border-black bg-gold/10" : "border-gray-300 hover:border-gray-400 bg-offwhite"].join(" ")}
                                                                title={!saveable ? `${preset.label} (preview – you don't own this skin yet)` : preset.label}
                                                            >
                                                                <div className="relative w-full aspect-video border border-gray-300 overflow-hidden shrink-0">
                                                                    <img
                                                                        src={preset.image}
                                                                        alt={preset.label}
                                                                        className="w-full h-full object-cover"
                                                                        style={{ imageRendering: 'pixelated' }}
                                                                    />
                                                                    {!saveable && (
                                                                        <div style={{
                                                                            position: 'absolute', inset: 0,
                                                                            backgroundColor: 'rgba(255,255,255,0.45)',
                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                            overflow: 'hidden', pointerEvents: 'none',
                                                                        }}>
                                                                            <span style={{
                                                                                transform: 'rotate(-35deg)',
                                                                                fontSize: '14px',
                                                                                fontFamily: "'Jersey 10', sans-serif",
                                                                                fontWeight: 900,
                                                                                color: 'rgba(0,0,0,0.82)',
                                                                                textTransform: 'uppercase',
                                                                                whiteSpace: 'nowrap',
                                                                                lineHeight: 1,
                                                                                userSelect: 'none',
                                                                            }}>PREVIEW</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <span className="font-['Jersey_10'] text-xs uppercase text-coal leading-none">{preset.label}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex-1 bg-coal_dark flex items-center justify-center overflow-hidden">
                                                {selectedBgSceneId && tiledScenePreviews.get(selectedBgSceneId) ? (
                                                    <img
                                                        src={tiledScenePreviews.get(selectedBgSceneId)}
                                                        alt="Scene preview"
                                                        className="max-w-full max-h-full block"
                                                        style={{ imageRendering: "pixelated" }}
                                                    />
                                                ) : selectedBgSceneId ? (
                                                    <div className="font-['Jersey_10'] text-xl text-white/40 animate-pulse">Generating preview…</div>
                                                ) : (
                                                    <div className="font-['Jersey_10'] text-xl text-white/25">Select an option</div>
                                                )}
                                            </div>
                                        )}
                                        <div className="px-4 py-3 bg-offwhite border-t border-black/10 flex items-center justify-between gap-4">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setCustomisationValue('pageBg', '');
                                                    setCustomisationValue('presetBgId', null);
                                                    setProfileBgImageUrl(null);
                                                    setPendingBgImageUrl(null);
                                                    setSelectedBgSceneId(null);
                                                    setShowBgModal(false);
                                                    toast.success('Background reset to default.');
                                                }}
                                                className="px-4 py-1.5 bg-offwhite text-coal font-['Jersey_10'] text-base uppercase border border-black/40 hover:bg-black/5 shrink-0"
                                            >Reset to Default</button>
                                            <button
                                                type="button"
                                                onClick={handleConfirmBgModal}
                                                disabled={bgModalMode === 'scene' && (!selectedBgSceneId || !tiledScenePreviews.get(selectedBgSceneId))}
                                                className="px-6 py-1.5 bg-gold text-coal font-['Jersey_10'] text-xl uppercase border border-black disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                                                style={{ boxShadow: "0 3px 0 0 rgba(0,0,0,0.4)" }}
                                            >Confirm</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {isOwnProfile && !guestMode && (
                    <div className={`fixed hidden md:block bottom-0 left-0 right-0 border-t border-t-[4px] border-black/50 transition-colors duration-200 ${isEditing ? 'bg-offwhite/90 border-gold' : 'bg-offwhite/50'} backdrop-blur-sm z-50`}>
                        <div className="px-4 lg2:px-6 py-3 flex items-center gap-4">
                            <div className="font-['Jersey_10'] text-xl leading-none text-coal">
                                {isEditing ? "Editing Profile" : "Viewing Profile"}
                            </div>
                            {isEditing && (
                                <button
                                    type="button"
                                    onClick={() => setBoxVisibilityOpen(true)}
                                    className="h-7 px-2 flex items-center gap-1.5 text-xs border border-black/50 rounded transition-colors bg-offwhite text-coal hover:bg-black/10"
                                >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                                    </svg>
                                    Box Visibility
                                    {(hiddenBoxSet.size + privateBoxSet.size) > 0 && <span className="bg-coal/80 text-offwhite text-[10px] px-1 rounded-full leading-none py-px">{hiddenBoxSet.size + privateBoxSet.size}</span>}
                                </button>
                            )}
                            {isEditing && (
                                <button
                                    type="button"
                                    onClick={() => setPreviewAsGuest(p => !p)}
                                    className={`h-7 px-2 flex items-center gap-1.5 text-xs border rounded transition-colors ${previewAsGuest ? 'bg-coal text-offwhite border-coal hover:bg-coal/80' : 'bg-offwhite text-coal border-black/50 hover:bg-black/10'}`}
                                >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
                                    </svg>
                                    Preview as guest
                                </button>
                            )}
                            <div className="flex items-center gap-4 ml-auto">
                                {isEditing && (
                                    <button
                                        type="button"
                                        onClick={handleOpenBgModal}
                                        className="h-7 px-2 flex items-center gap-1.5 text-xs border border-black/50 rounded transition-colors bg-offwhite text-coal hover:bg-black/10"
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
                                        </svg>
                                        Background
                                        {profileBgImageUrl && (
                                            <span className="w-3 h-3 rounded-full border border-black/30 shrink-0 overflow-hidden inline-block">
                                                <img src={profileBgImageUrl} alt="" className="w-full h-full object-cover" />
                                            </span>
                                        )}
                                        {!profileBgImageUrl && sanitiseHex(customisation?.pageBg) && (
                                            <span className="w-3 h-3 rounded-full border border-black/30 shrink-0 inline-block" style={{ backgroundColor: sanitiseHex(customisation?.pageBg) }} />
                                        )}
                                    </button>
                                )}
                                {isEditing && (
                                    <div className="flex items-center gap-2">
                                        <div className="text-coal/90 text-xs">Profile visibility</div>
                                        <select
                                            value={customisation?.profileVisibility ?? 'private'}
                                            onChange={(e) => setCustomisationValue('profileVisibility', e.target.value)}
                                            className="h-7 px-2 text-xs border border-black/50 bg-offwhite text-coal rounded cursor-pointer"
                                        >
                                            <option value="private">Private (login required)</option>
                                            <option value="public">Public (visible to guests)</option>
                                        </select>
                                    </div>
                                )}
                                {activeRestrictions?.restrictCustomisation ? (
                                    <div className="px-4 py-2 border border-black/50 text-sm rounded bg-black/10 text-coal/70">
                                        You are restricted from editing your profile.
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        {isEditing && (
                                            <button
                                                type="button"
                                                onClick={() => requestConfirm({
                                                    title: 'Cancel & Reset?',
                                                    message: 'All unsaved changes will be lost, including any banner or top friends changes. Are you sure?',
                                                    onConfirm: () => {
                                                        applyCustomisationPayload(originalCustomisation);
                                                        setBannerUrl(originalBannerUrl);
                                                        setPendingBannerDataUrl(null);
                                                        setOriginalBannerUrl(null);
                                                        setProfileBgImageUrl(originalBgImageUrl);
                                                        setPendingBgImageUrl(undefined);
                                                        setOriginalBgImageUrl(null);
                                                        setOriginalCustomisation(null);
                                                        setIsEditing(false);
                                                        setEditingHeader(null);
                                                        setPreviewAsGuest(false);
                                                    },
                                                })}
                                                className="px-4 py-2 border border-black/50 text-sm rounded transition-colors duration-150 bg-black/20 text-coal hover:bg-red-900/60 hover:text-offwhite hover:border-red-900/80"
                                            >
                                                Cancel &amp; Reset
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={handleEditToggle}
                                            className={`px-4 py-2 border border-black/50 text-sm rounded transition-colors duration-150 ${
                                                isEditing ? 'bg-offwhite text-coal hover:bg-gold hover:text-coal' : 'bg-black/60 text-offwhite hover:bg-black/80'
                                            }`}
                                        >
                                            {isEditing ? "Save Changes" : "Edit Profile"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <EmotePickerModal open={isEmotePickerOpen} onClose={closeEmotePicker} onSelect={handleEmoteSelected} />

                {/* ─── Box Visibility Modal ────────────────────────────────────────────── */}
                {boxVisibilityOpen && (() => {
                    const allLayoutKeys = [
                        ...normalisedLayout.left.filter(k => !LOCKED_BOXES.has(k)),
                        ...normalisedLayout.right.filter(k => !LOCKED_BOXES.has(k)),
                    ];
                    const lockedKeys = [
                        'bio',
                        ...normalisedLayout.left.filter(k => LOCKED_BOXES.has(k)),
                        ...normalisedLayout.right.filter(k => LOCKED_BOXES.has(k)),
                    ];
                    const hiddenKeys = allLayoutKeys.filter(k => !SOFT_LOCKED_BOXES.has(k) && boxVisibilityMap[k] === 'hidden');
                    const privateKeys = allLayoutKeys.filter(k => boxVisibilityMap[k] === 'private');
                    const visibleKeys = [
                        ...allLayoutKeys.filter(k => !boxVisibilityMap[k] || boxVisibilityMap[k] === 'visible'),
                        ...lockedKeys,
                    ];
                    const getBoxMeta = (key) => {
                        const box = BOX_REGISTRY.find(b => b.key === key);
                        const name = key === 'bio' ? (getHeaderTitle('bio') || 'Bio') : (getHeaderTitle(key) || box?.label || key);
                        const desc = key === 'bio' ? 'Your bio & avatar' : (box?.description ?? null);
                        return { name, desc };
                    };
                    const spacer = <div className="w-6 h-6 shrink-0" />;
                    const arrowBtn = (label, onClick, title) => (
                        <button type="button" onClick={onClick} title={title}
                                className="w-6 h-6 shrink-0 flex items-center justify-center text-sm text-coal/40 hover:text-coal hover:bg-black/10 rounded transition-colors">
                            {label}
                        </button>
                    );
                    const handleDeleteGallery = (key) => {
                        requestConfirm({
                            title: 'Delete this gallery box?',
                            message: 'This gallery box will be permanently removed from your profile. Images stay safe in your FranklyCloud.',
                            onConfirm: () => removeGalleryBox(key),
                        });
                    };
                    const BoxRow = ({ keyName, left, right }) => {
                        const { name, desc } = getBoxMeta(keyName);
                        const isGalleryRow = isGalleryKey(keyName);
                        return (
                            <div className="flex items-center gap-1 px-2 py-1.5 border-b border-black/10 last:border-0">
                                {left}
                                <div className="flex-1 min-w-0 text-center">
                                    <div className="text-xs font-bold text-coal truncate leading-tight">{renderLabelWithEmotes(name)}</div>
                                    {desc && <div className="text-[10px] text-coal/40 truncate leading-tight">{desc}</div>}
                                </div>
                                {isGalleryRow && (
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteGallery(keyName)}
                                        title="Delete this gallery box"
                                        className="w-6 h-6 shrink-0 flex items-center justify-center text-coal/40 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                    >
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6"/>
                                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                            <path d="M10 11v6M14 11v6"/>
                                            <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                                        </svg>
                                    </button>
                                )}
                                {right}
                            </div>
                        );
                    };
                    return (
                        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onMouseDown={(e) => { if (e.target === e.currentTarget) setBoxVisibilityOpen(false); }}>
                            <div className="bg-offwhite border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,0.4)] w-[580px] max-h-[80vh] flex flex-col overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-3 border-b border-black/20 bg-coal text-offwhite">
                                    <span className="font-['Jersey_10'] text-xl uppercase tracking-wide">Box Visibility</span>
                                    <button type="button" onClick={() => setBoxVisibilityOpen(false)} className="text-offwhite hover:text-gold transition-colors text-lg leading-none">✕</button>
                                </div>
                                {/* Column headers */}
                                <div className="grid grid-cols-3 divide-x divide-black/20 border-b border-black/20 bg-black/[0.04]">
                                    {[
                                        { label: 'Hidden', desc: 'Not shown to anyone' },
                                        { label: 'Only Me', desc: 'Shown on your own profile only' },
                                        { label: 'Visible', desc: 'Shown to everyone' },
                                    ].map(({ label, desc }) => (
                                        <div key={label} className="px-3 py-2 text-center">
                                            <div className="font-['Jersey_10'] text-sm uppercase text-coal tracking-wide">{label}</div>
                                            <div className="text-[10px] text-coal/50">{desc}</div>
                                        </div>
                                    ))}
                                </div>
                                {/* Columns */}
                                <div className="grid grid-cols-3 divide-x divide-black/20 flex-1 overflow-hidden">
                                    {/* Hidden column */}
                                    <div className="overflow-y-auto">
                                        {hiddenKeys.map(key => (
                                            <BoxRow key={key} keyName={key}
                                                    left={spacer}
                                                    right={arrowBtn('→', () => setBoxVisibility(key, 'private'), 'Move to Only Me')}
                                            />
                                        ))}
                                        {hiddenKeys.length === 0 && (
                                            <div className="px-3 py-4 text-[11px] text-coal/30 text-center italic">None</div>
                                        )}
                                    </div>
                                    {/* Only Me column */}
                                    <div className="overflow-y-auto">
                                        {privateKeys.map(key => {
                                            const softLocked = SOFT_LOCKED_BOXES.has(key);
                                            return (
                                                <BoxRow key={key} keyName={key}
                                                        left={softLocked
                                                            ? <div className="w-6 h-6 shrink-0 flex items-center justify-center" title="Cannot be hidden"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-coal/25"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></div>
                                                            : arrowBtn('←', () => setBoxVisibility(key, 'hidden'), 'Move to Hidden')}
                                                        right={arrowBtn('→', () => setBoxVisibility(key, 'visible'), 'Move to Visible')}
                                                />
                                            );
                                        })}
                                        {privateKeys.length === 0 && (
                                            <div className="px-3 py-4 text-[11px] text-coal/30 text-center italic">None</div>
                                        )}
                                    </div>
                                    {/* Visible column */}
                                    <div className="overflow-y-auto">
                                        {visibleKeys.map(key => {
                                            const fullyLocked = LOCKED_BOXES.has(key) || key === 'bio';
                                            return (
                                                <BoxRow key={key} keyName={key}
                                                        left={fullyLocked
                                                            ? spacer
                                                            : arrowBtn('←', () => setBoxVisibility(key, 'private'), 'Move to Only Me')}
                                                        right={fullyLocked
                                                            ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-coal/25 shrink-0"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                                            : spacer}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                                {/* Add Gallery Box bar */}
                                <div className="flex items-center justify-between gap-3 px-3 py-2 border-t border-black/20 bg-black/[0.02]">
                                    <div className="flex flex-col min-w-0">
                                        <span className="font-['Jersey_10'] text-sm uppercase text-coal tracking-wide leading-none">Gallery Boxes</span>
                                        <span className="text-[10px] text-coal/50 leading-tight">Show off images from your FranklyCloud. Up to {MAX_GALLERY_BOXES} per profile.</span>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className="text-[10px] text-coal/50 whitespace-nowrap">{galleryIdsOrdered.length}/{MAX_GALLERY_BOXES}</span>
                                        <button
                                            type="button"
                                            onClick={() => addGalleryBox()}
                                            disabled={galleryIdsOrdered.length >= MAX_GALLERY_BOXES}
                                            className="px-3 py-1 text-[11px] font-bold border border-black/40 bg-offwhite text-coal rounded hover:bg-gold transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                                            title={galleryIdsOrdered.length >= MAX_GALLERY_BOXES ? `Maximum of ${MAX_GALLERY_BOXES} gallery boxes reached` : 'Add a new gallery box'}
                                        >
                                            + Add Gallery Box
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* ─── Circle Background Modal ─────────────────────────────────────────── */}
                {circleBgModalOpen && (
                    <div
                        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60"
                        onMouseDown={(e) => { if (e.target === e.currentTarget) setCircleBgModalOpen(false); }}
                    >
                        <div className="bg-offwhite border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,0.4)] w-[340px] max-h-[85vh] flex flex-col overflow-hidden">
                            {/* Header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b border-black/20 bg-coal text-offwhite">
                                <span className="font-['Jersey_10'] text-xl uppercase tracking-wide">Avatar Background</span>
                                <button type="button" onClick={() => setCircleBgModalOpen(false)} className="text-offwhite hover:text-gold transition-colors text-lg leading-none">✕</button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-5">
                                {/* Colour picker section */}
                                <div>
                                    <p className="font-['Jersey_10'] text-base uppercase text-coal/70 mb-2 tracking-wide">Colour</p>
                                    <div className="flex items-center gap-3">
                                        <ColorPicker
                                            value={circleBgDraftColor || '#ffffff'}
                                            onChange={(e) => {
                                                setCircleBgDraftColor(e.target.value);
                                                setCircleBgDraftImage(null);
                                            }}
                                            className="h-12 w-full p-0 border-2 border-black/40 rounded cursor-pointer"
                                            title="Pick a colour"
                                        />
                                    </div>
                                    {!circleBgDraftImage && (
                                        <div className="mt-2 flex items-center gap-2">
                                            <div
                                                className="w-8 h-8 rounded-full border border-black/40 shrink-0"
                                                style={{ backgroundColor: circleBgDraftColor || '#ffffff' }}
                                            />
                                            <span className="text-xs text-coal/60 font-mono">{circleBgDraftColor || '#ffffff'}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Unlocked backgrounds section */}
                                <div>
                                    <p className="font-['Jersey_10'] text-base uppercase text-coal/70 mb-2 tracking-wide">Unlocked Backgrounds</p>
                                    {!myCircleBgsLoaded ? (
                                        <p className="text-xs text-coal/50 italic">Loading…</p>
                                    ) : myCircleBgs.length === 0 ? (
                                        <p className="text-xs text-coal/50 italic">You haven't unlocked any backgrounds yet. Check the Framp Shack!</p>
                                    ) : (
                                        <>
                                            <style>{`.habbo-scroll-h::-webkit-scrollbar{width:16px;height:16px}.habbo-scroll-h::-webkit-scrollbar-track{background:#cfcfcf;border:1px solid #000}.habbo-scroll-h::-webkit-scrollbar-thumb{background:#8f8f8f;border:1px solid #000}.habbo-scroll-h::-webkit-scrollbar-thumb:hover{background:#7a7a7a}`}</style>
                                            <div className="habbo-scroll-h flex flex-row gap-2 w-full overflow-x-scroll overflow-y-hidden pb-1" style={{ scrollbarWidth: 'auto' }}>
                                                {myCircleBgs.map((bg) => {
                                                    const isSelected = circleBgDraftImage?.BgID === bg.BgID;
                                                    return (
                                                        <button
                                                            key={bg.BgID}
                                                            type="button"
                                                            onClick={() => {
                                                                setCircleBgDraftImage(bg);
                                                                setCircleBgDraftColor('');
                                                            }}
                                                            className="flex flex-col items-center gap-1 shrink-0 w-[92px] min-w-0"
                                                            title={bg.name}
                                                        >
                                                            <div className={`relative w-[92px] h-[92px] rounded overflow-hidden border-2 transition-all ${isSelected ? 'border-gold shadow-[0_0_0_2px_rgba(0,0,0,0.6)]' : 'border-black/30 hover:border-black/60'}`}>
                                                                <img src={resolveCosmeticUrl(bg.imageURL)} alt={bg.name} className="w-full h-full object-cover" style={{ imageRendering: 'pixelated' }} />
                                                                {isSelected && (
                                                                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <span className="w-full truncate text-center text-sm text-coal font-['Jersey_10'] uppercase leading-none tracking-widest">{bg.name}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Preview */}
                                {(() => {
                                    const draftBgStyle = circleBgDraftImage
                                        ? { backgroundImage: `url(${resolveCosmeticUrl(circleBgDraftImage.imageURL)})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                                        : { backgroundColor: circleBgDraftColor || '#ffffff' };
                                    const friendHex = safeHexOrFallback(circleBgDraftImage ? circleBgDraftImage.imageURL : (circleBgDraftColor || '#ffffff'));
                                    const friendText = luminance(friendHex) < 0.16 ? '#f5f5f0' : '#222222';
                                    const tabs = [
                                        { id: 'bio', label: 'Bio' },
                                        { id: 'post', label: 'Post' },
                                        { id: 'friends', label: 'Friends' },
                                    ];
                                    return (
                                        <div>
                                            <p className="font-['Jersey_10'] text-base uppercase text-coal/70 mb-2 tracking-wide">Preview</p>
                                            <div className="flex border border-black/20 rounded overflow-hidden mb-2">
                                                {tabs.map((t) => (
                                                    <button
                                                        key={t.id}
                                                        type="button"
                                                        onClick={() => setCircleBgPreviewTab(t.id)}
                                                        className={`flex-1 px-1 py-1 text-sm font-['Jersey_10'] uppercase tracking-wide leading-none transition-colors ${circleBgPreviewTab === t.id ? 'bg-coal text-offwhite' : 'bg-offwhite text-coal/60 hover:bg-black/5'}`}
                                                    >
                                                        {t.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="border border-black/15 bg-[#ededed]/40 rounded p-3 flex items-center justify-center min-h-[124px] overflow-hidden">
                                                {circleBgPreviewTab === 'bio' && (
                                                    <div className="w-[92px] h-[92px] rounded border border-black/50 flex items-center justify-center overflow-hidden" style={draftBgStyle}>
                                                        <HabboImage habbo={profileUsername} hotel="COM" size="b" direction={2} head_direction={2} gesture="sml" className="relative mt-14" style={{ imageRendering: 'pixelated', scale: 2 }} alt="preview" />
                                                    </div>
                                                )}
                                                {circleBgPreviewTab === 'post' && (
                                                    <div className="w-full max-w-[290px] bg-white rounded border border-black/10 overflow-hidden">
                                                        {/* post row */}
                                                        <div className="p-2 flex gap-2">
                                                            <div className="w-[48px] h-[48px] border-2 rounded border-black/10 shrink-0 overflow-hidden flex items-center justify-center" style={draftBgStyle}>
                                                                <HabboImage habbo={profileUsername} hotel="COM" size="a" direction={2} head_direction={2} gesture="sml" headonly="true" />
                                                            </div>
                                                            <div className="flex-1 min-w-0 flex flex-col">
                                                                <span className="font-bold text-coal leading-none">{profileUsername}</span>
                                                                <p className="text-coal text-sm break-words leading-tight mt-1">Loving the new profile look!</p>
                                                            </div>
                                                        </div>
                                                        {/* comment row */}
                                                        <div className="border-t border-black/10 pl-[22px]">
                                                            <div className="flex gap-2 p-2">
                                                                <div className="w-[30px] h-[30px] relative rounded border border-black/20 overflow-hidden shrink-0" style={draftBgStyle}>
                                                                    <HabboImage habbo={profileUsername} hotel="COM" size="a" direction={2} head_direction={2} gesture="sml" headonly className="absolute top-5 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-none" />
                                                                </div>
                                                                <div className="flex-1 min-w-0 flex flex-col">
                                                                    <span className="font-bold text-sm text-coal leading-none">{profileUsername}</span>
                                                                    <p className="text-xs text-coal/85 mt-0.5 break-words leading-snug">Nice one!</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                {circleBgPreviewTab === 'friends' && (
                                                    <div className="flex items-end justify-center gap-5">
                                                        <div className="flex flex-col items-center gap-1">
                                                            <div className="relative flex flex-col items-center justify-center overflow-hidden" style={{ ...draftBgStyle, width: '72px', aspectRatio: '3 / 4' }}>
                                                                <div className="relative pointer-events-none top-[-8px]">
                                                                    <HabboImage habbo={profileUsername} hotel="COM" size="a" direction={2} head_direction={3} gesture="sml" style={{ imageRendering: 'pixelated', display: 'block' }} />
                                                                </div>
                                                                <div className="absolute bottom-1 left-0 right-0 font-['Jersey_10'] text-base text-center leading-none truncate px-0.5 pointer-events-none tracking-widest" style={{ color: friendText }}>{profileUsername}</div>
                                                            </div>
                                                            <span className="text-[10px] text-coal/50 uppercase tracking-wide">Small</span>
                                                        </div>
                                                        <div className="flex flex-col items-center gap-1">
                                                            <div className="relative flex flex-col items-center justify-center overflow-hidden" style={{ ...draftBgStyle, width: '130px', aspectRatio: '1 / 1' }}>
                                                                <div className="relative pointer-events-none top-[-8px]">
                                                                    <HabboImage habbo={profileUsername} hotel="COM" size="b" direction={2} head_direction={3} gesture="sml" style={{ imageRendering: 'pixelated', display: 'block' }} />
                                                                </div>
                                                                <div className="absolute bottom-1 left-0 right-0 font-['Jersey_10'] text-base text-center leading-none truncate px-0.5 pointer-events-none tracking-widest" style={{ color: friendText }}>{profileUsername}</div>
                                                            </div>
                                                            <span className="text-[10px] text-coal/50 uppercase tracking-wide">Wide</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {circleBgDraftImage && (
                                                <p className="text-xs text-center text-coal/60 mt-1">{circleBgDraftImage.name}</p>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Footer buttons */}
                            <div className="flex gap-2 p-3 border-t border-black/20">
                                <button
                                    type="button"
                                    onClick={() => setCircleBgModalOpen(false)}
                                    className="flex-1 py-2 border border-black/40 bg-offwhite text-coal font-['Jersey_10'] text-lg uppercase hover:bg-black/10 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCircleBgConfirm}
                                    className="flex-1 py-2 border border-black bg-coal text-offwhite font-['Jersey_10'] text-lg uppercase hover:bg-gold hover:text-coal transition-colors"
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {/* ─────────────────────────────────────────────────────────────────────── */}
            </>
        </>
    );
}

export default Profile;
